/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import './QuestionOperations.css';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';
import MathText from './MathText';

const QuestionManagement = () => {
    const navigate = useNavigate();
    // Global state
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
        const saved = localStorage.getItem('questionOperations_currentIndex');
        return saved ? parseInt(saved) : 0;
    });
    const [uploadedFile, setUploadedFile] = useState(null);
    const [selectedExamType, setSelectedExamType] = useState(() => {
        const saved = localStorage.getItem('questionOperations_examType');
        return saved || '';
    });
    const [bookletCount, setBookletCount] = useState(() => {
        const saved = localStorage.getItem('questionOperations_bookletCount');
        return saved ? parseInt(saved) : 2;
    });
    const [isUploading, setIsUploading] = useState(false);
    const [showMappingWarning, setShowMappingWarning] = useState(false);
    const [warningCountdown, setWarningCountdown] = useState(3);
    const [warningProgress, setWarningProgress] = useState(0);
    const [activeTab, setActiveTab] = useState('upload'); // Başlangıçta her zaman upload tabında başla
    const [fileInfo, setFileInfo] = useState(() => {
        const saved = localStorage.getItem('questionOperations_fileInfo');
        return saved ? JSON.parse(saved) : null;
    });
    const [learningOutcomes, setLearningOutcomes] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [programOutcomes, setProgramOutcomes] = useState([]);
    const [loadingOutcomes, setLoadingOutcomes] = useState(false);
    
    // Yapay zeka Önerileri için state'ler
    const [aiMatches, setAiMatches] = useState([]);
    const [loadingAI, setLoadingAI] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [aiError, setAiError] = useState(null);
    const aiControllerRef = useRef(null);

    // Soru değiştiğinde AI önerilerini temizle ve pending AI çağrılarını iptal et
    useEffect(() => {
        // Önceki AI analizini iptal et
        if (aiControllerRef.current) {
            aiControllerRef.current.abort();
            aiControllerRef.current = null;
        }
        
        setAiMatches([]);
        setAiError(null);
        setLoadingAI(false);
    }, [currentQuestionIndex]);

    // Component unmount olduğunda tüm işlemleri temizle (Navigation için kritik!)
    useEffect(() => {
        return () => {
            // Tüm pending AI çağrılarını iptal et
            if (aiControllerRef.current) {
                aiControllerRef.current.abort();
            }
            
            // Loading state'lerini temizle
            setLoadingAI(false);
            setLoadingOutcomes(false);
            setIsUploading(false);
            
            // Modal/warning state'lerini temizle (navigation engelini kaldır)
            setShowMappingWarning(false);
            
            console.log('QuestionOperations component unmounted - tüm işlemler temizlendi');
        };
    }, []);
    
    // Yeni state'ler
    const [selectedProgram, setSelectedProgram] = useState(() => {
        const saved = localStorage.getItem('questionOperations_selectedProgram');
        return saved ? JSON.parse(saved) : null;
    });
    const [selectedCourse, setSelectedCourse] = useState(() => {
        const saved = localStorage.getItem('questionOperations_selectedCourse');
        return saved ? JSON.parse(saved) : null;
    });
    const [academicCourses, setAcademicCourses] = useState([]);
    const [isCourseMenuOpen, setIsCourseMenuOpen] = useState(false);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [academicId, setAcademicId] = useState(() => {
        const saved = localStorage.getItem('questionOperations_academicId');
        return saved || null;
    });
    const [expandedQuestionMapping, setExpandedQuestionMapping] = useState(null); // Hangi soru için mapping açık
    const [questionAiMatches, setQuestionAiMatches] = useState({}); // { questionId: [matches] }
    const [questionAiLoading, setQuestionAiLoading] = useState({}); // { questionId: boolean }
    const [swapConfirm, setSwapConfirm] = useState(null); // { questionId, conflictingQuestion, letter, idx, newValue, oldValue }
    const [deleteConfirm, setDeleteConfirm] = useState(false); // Toplu silme onay modalı
    const [isDeleting, setIsDeleting] = useState(false); // Silme işlemi devam ediyor mu
    const [bulkAiLoading, setBulkAiLoading] = useState(false); // Genel AI öner ve uygula butonu için loading
    const [bulkAiProgress, setBulkAiProgress] = useState(0); // Progress yüzdesi (0-100)
    const [loProgramMappings, setLoProgramMappings] = useState({}); // { [outcomeId]: [programOutcomeIds] }

    // Kitapçık eşlemesi (A↔B) için state
    const [bookletMappings, setBookletMappings] = useState({}); // { [questionId]: bQuestionNumber }
    const bookletSelectRef = useRef(null);
    const loMappingLoadingRef = useRef(new Set());

    const getBookletStorageKey = () => {
        const pid = selectedProgram?.programId || 'p';
        const cid = selectedCourse?.courseId || 'c';
        const et = selectedExamType || 't';
        return `questionOperations_bookletMap_${pid}_${cid}_${et}`;
    };

    const learningOutcomeMap = useMemo(() => {
        const map = {};
        (learningOutcomes || []).forEach((outcome) => {
            if (!outcome) return;
            if (outcome.code) {
                map[outcome.code] = outcome;
            }
            if (outcome.id !== undefined && outcome.id !== null) {
                map[String(outcome.id)] = outcome;
            }
        });
        return map;
    }, [learningOutcomes]);

    const programOutcomeMap = useMemo(() => {
        const map = {};
        (programOutcomes || []).forEach((outcome) => {
            if (!outcome) return;
            if (outcome.code) {
                map[outcome.code] = outcome;
            }
            if (outcome.id !== undefined && outcome.id !== null) {
                map[String(outcome.id)] = outcome;
            }
        });
        return map;
    }, [programOutcomes]);

    // Seçim değiştiğinde ilgili eşleme verisini yükle
    useEffect(() => {
        try {
            const raw = localStorage.getItem(getBookletStorageKey());
            setBookletMappings(raw ? JSON.parse(raw) : {});
        } catch (e) {
            setBookletMappings({});
        }
    }, [selectedProgram, selectedCourse, selectedExamType]);

    // Kitapçık eşlemesi değiştiğinde UI'ı güncelle - Mapping tab kaldırıldı
    // useEffect kaldırıldı

    const saveBookletMappings = (next) => {
        try { localStorage.setItem(getBookletStorageKey(), JSON.stringify(next)); } catch (e) {}
    };

    const handleBookletMapChange = async (questionId, bNumber) => {
        setBookletMappings(prev => {
            const next = { ...prev, [questionId]: bNumber };
            saveBookletMappings(next);
            return next;
        });
        try {
            const q = questions.find(x => String(x.id) === String(questionId));
            if (q) {
                await axios.put(`${API_ENDPOINTS.UPDATE_QUESTION}`, {
                    id: q.id,
                    text: q.text,
                    score: q.score,
                    outcomes: q.outcomes,
                    examType: selectedExamType,
                    bookletMap: Number(bookletCount) === 2 ? bNumber : null
                });
            }
        } catch (e) {
            try { toast.error('Kitapçık eşlemesi kaydedilemedi'); } catch (_) {}
        }
        // UI otomatik güncellenecek (useEffect sayesinde)
    };

    const clearBookletMapFor = (questionId) => {
        setBookletMappings(prev => {
            const next = { ...prev };
            delete next[questionId];
            saveBookletMappings(next);
            return next;
        });
        // DB'den de temizle
        try {
            const q = questions.find(x => String(x.id) === String(questionId));
            if (q) {
                axios.put(`${API_ENDPOINTS.UPDATE_QUESTION}`, {
                    id: q.id,
                    text: q.text,
                    score: q.score,
                    outcomes: q.outcomes,
                    examType: selectedExamType,
                    bookletMap: null
                }).catch(() => {});
            }
        } catch (e) {}
        // Temizlendikten sonra dropdown'ı otomatik aç (focus yeterli)
        try { setTimeout(() => { bookletSelectRef.current && bookletSelectRef.current.focus && bookletSelectRef.current.focus(); }, 0); } catch (e) {}
        // UI otomatik güncellenecek (useEffect sayesinde)
    };

    // Tek menü: Atanan derslerin benzersiz listesi
    const uniqueAssignedCourses = useMemo(() => {
        const seen = new Set();
        const list = [];
        (academicCourses || []).forEach(c => {
            const key = String(c.courseId);
            if (!seen.has(key)) {
                seen.add(key);
                list.push(c);
            }
        });
        return list.sort((a, b) => (
            (a.courseName || '').localeCompare(b.courseName || '', 'tr', { sensitivity: 'base' }) ||
            (a.programName || '').localeCompare(b.programName || '', 'tr', { sensitivity: 'base' })
        ));
    }, [academicCourses]);

    const formatCourseLabel = (c) => {
        if (!c) return '';
        const programPart = c.programName ? `(${c.programName}${c.educationTypeName ? ` - ${c.educationTypeName}` : ''})` : '';
        const hasMyo = String(c.facultyName || '').toUpperCase().includes('MESLEK') || String(c.facultyName || '').toUpperCase().includes('MYO');
        const facultyPrefix = hasMyo ? 'MYO' : 'Fakülte';
        const facultyPart = c.facultyName ? ` - ${facultyPrefix} : ${c.facultyName}` : '';
        return `${c.courseName || ''} ${programPart}${facultyPart}`.trim();
    };
    
    // Mapping kontrolü state'leri
    const [mappingStatus, setMappingStatus] = useState(null);
    const [showMappingError, setShowMappingError] = useState(false);
    const [mappingErrorMessage, setMappingErrorMessage] = useState('');
    const userSelectedCourseRef = useRef(false);
    
    // Mapping status'u yenileme fonksiyonu
    const refreshMappingStatus = async () => {
        if (selectedProgram && selectedCourse) {
            console.log('🔄 Mapping status yenileniyor...');
            await checkOutcomeMappings(
                selectedProgram.programId,
                selectedCourse.courseId,
                userSelectedCourseRef.current
            );
        }
    };
    // Bu derse ait tanımlı sınav türleri (exam_weights'ten)
    const [availableExamTypes, setAvailableExamTypes] = useState([]); // kanonik
    const [availableExamTypesRaw, setAvailableExamTypesRaw] = useState([]); // orijinal metin
    
    // Soru düzenleme state'leri
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [editForm, setEditForm] = useState({
        text: '',
        score: ''
    });

    // Statik etiketler (ad ve renk), listeleme availableExamTypes ile sınırlı olacak
    const examTypes = [
        { id: "vize", name: "Vize Sınavı", color: "badge-blue" },
        { id: "final", name: "Final Sınavı", color: "badge-green" },
        { id: "but", name: "Bütünleme Sınavı", color: "badge-orange" },
        { id: "quiz", name: "Quiz", color: "badge-blue" },
        { id: "pro", name: "Pro", color: "badge-blue" }
    ];

    // Örnek şablon indirme fonksiyonu
    const downloadSampleTemplate = () => {
        const link = document.createElement('a');
        link.href = '/OrnekSablon.xlsx';
        link.download = 'OrnekSablon.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Kitapçık sayısı değişimi
    const handleBookletCountChange = (value) => {
        const v = parseInt(value);
        const safe = isNaN(v) || v < 1 ? 1 : v > 4 ? 4 : v;
        setBookletCount(safe);
        try { localStorage.setItem('questionOperations_bookletCount', String(safe)); } catch (e) {}
    };



    // Dersin öğrenme çıktılarını veritabanından çek
    const fetchLearningOutcomes = async (courseId, signal) => {
        console.log('fetchLearningOutcomes başladı, gelen courseId:', courseId, 'tipi:', typeof courseId);
        
        if (!courseId) {
            console.log('fetchLearningOutcomes: courseId yok');
            setLearningOutcomes([]);
            return;
        }
        
        // courseId'nin string olup olmadığını kontrol et
        if (typeof courseId === 'string' && courseId.trim() === '') {
            console.log('fetchLearningOutcomes: courseId boş string');
            setLearningOutcomes([]);
            return;
        }
        
        // courseId'nin geçerli bir sayı olduğundan emin ol
        const numericCourseId = parseInt(courseId);
        console.log('parseInt sonucu:', numericCourseId, 'isNaN:', isNaN(numericCourseId));
        
        if (isNaN(numericCourseId) || numericCourseId <= 0) {
            console.error('fetchLearningOutcomes: Geçersiz courseId:', courseId);
            setLearningOutcomes([]);
            return;
        }
        
        try {
            setLoadingOutcomes(true);
            console.log('fetchLearningOutcomes çağrıldı, courseId:', numericCourseId);
            const response = await axios.get(API_ENDPOINTS.COURSE_OUTCOMES(numericCourseId), {
                signal: signal // Abort signal ekle
            });
            
            if (response.data && response.data.learning) {
                console.log('Learning outcomes API yanıtı:', response.data);
                // Dersin öğrenme çıktılarını numara sırasına göre sırala
                const sortedOutcomes = response.data.learning.sort((a, b) => {
                    const aNum = parseInt(a.code.replace('ÖÇ', ''));
                    const bNum = parseInt(b.code.replace('ÖÇ', ''));
                    return aNum - bNum;
                });
                console.log('Sıralanmış learning outcomes:', sortedOutcomes);
                setLearningOutcomes(sortedOutcomes);
            } else {
                console.log('API yanıtında learning outcomes yok');
                setLearningOutcomes([]);
            }
        } catch (error) {
            // Abort edilmişse hata loglama
            if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
                console.log('fetchLearningOutcomes iptal edildi');
            } else {
                console.error('Dersin öğrenme çıktıları yüklenirken hata:', error);
                setLearningOutcomes([]);
            }
        } finally {
            setLoadingOutcomes(false);
        }
    };

    // Dersin öğrenme çıktılarını yükle (seçilen derse göre)
    useEffect(() => {
        const controller = new AbortController();
        
        if (selectedCourse && selectedCourse.courseId) {
            console.log('Başlangıçta learning outcomes yükleniyor...');
            console.log('selectedCourse:', selectedCourse);
            fetchLearningOutcomes(selectedCourse.courseId, controller.signal);
        }

        // Cleanup: ders değiştiğinde veya component unmount olduğunda
        return () => {
            controller.abort();
        };
    }, [selectedCourse]);

    // Sınav türü normalizasyonu (kanonik kimliklere çevirir)
    const normalizeExamTypeCanon = (value) => {
        const v = String(value || '').trim().toLowerCase();
        if (v.includes('vize') || v === 'midterm') return 'vize';
        if (v.includes('final')) return 'final';
        if (v.includes('but') || v.includes('bütün') || v.includes('butun') || v.includes('bütü') || v.includes('butu') || v.includes('resit')) return 'but';
        if (v.includes('quiz') || v.includes('kısa') || v.includes('kisa')) return 'quiz';
        if (v === 'pro') return 'pro';
        return v;
    };

    // Seçilen program ve derse göre sınav türlerini yükle (exam_weights)
    useEffect(() => {
        const loadExamTypes = async () => {
            try {
                if (!(selectedProgram && selectedProgram.programId && selectedCourse && selectedCourse.courseId)) {
                    setAvailableExamTypes([]);
                    setAvailableExamTypesRaw([]);
                    return;
                }
                const programId = selectedProgram.programId;
                const courseId = selectedCourse.courseId;
                const res = await axios.get(API_ENDPOINTS.GET_EXAM_WEIGHTS(programId, courseId));
                const items = (res.data && res.data.weights) ? res.data.weights : [];
                const raws = Array.from(new Set(items.map(w => String(w.type || '').trim()))).filter(Boolean);
                const canons = Array.from(new Set(raws.map(t => normalizeExamTypeCanon(t)))).filter(Boolean);
                setAvailableExamTypesRaw(raws);
                setAvailableExamTypes(canons);
            } catch (err) {
                console.error('Exam weights alınırken hata:', err);
                setAvailableExamTypes([]);
                setAvailableExamTypesRaw([]);
            }
        };
        loadExamTypes();
    }, [selectedProgram, selectedCourse]);

    // Geçersiz kalan sınav türü seçimini temizle
    useEffect(() => {
        try {
            if (selectedExamType && !availableExamTypes.includes(selectedExamType)) {
                localStorage.removeItem('questionOperations_examType');
                handleExamTypeChange('');
            }
        } catch (e) {}
    }, [availableExamTypes]);

    // Program çıktılarını veritabanından çek
    const fetchProgramOutcomes = async (programId) => {
        if (!programId) {
            console.log('fetchProgramOutcomes: programId yok');
            return;
        }
        
        // programId'nin geçerli bir sayı olduğundan emin ol
        const numericProgramId = parseInt(programId);
        if (isNaN(numericProgramId) || numericProgramId <= 0) {
            console.error('fetchProgramOutcomes: Geçersiz programId:', programId);
            setProgramOutcomes([]);
            return;
        }
        
        try {
            setLoadingOutcomes(true);
            console.log('fetchProgramOutcomes çağrıldı, programId:', numericProgramId);
            const response = await axios.get(API_ENDPOINTS.PROGRAM_OUTCOMES(numericProgramId));
            
            if (response.data && response.data.outcomes) {
                console.log('Program outcomes API yanıtı:', response.data);
                // Program çıktılarını numara sırasına göre sırala
                const sortedOutcomes = response.data.outcomes.sort((a, b) => {
                    const aNum = parseInt(a.code.replace('PÇ', ''));
                    const bNum = parseInt(b.code.replace('PÇ', ''));
                    return aNum - bNum;
                });
                console.log('Sıralanmış program outcomes:', sortedOutcomes);
                setProgramOutcomes(sortedOutcomes);
            } else {
                console.log('API yanıtında program outcomes yok');
                setProgramOutcomes([]);
            }
        } catch (error) {
            console.error('Program çıktıları yüklenirken hata:', error);
            setProgramOutcomes([]);
        } finally {
            setLoadingOutcomes(false);
        }
    };

    const loadOutcomeMapping = useCallback(async (outcomeId) => {
        if (!selectedProgram?.programId || !selectedCourse?.courseId || !outcomeId) {
            return;
        }

        const cacheKey = String(outcomeId);
        if (loMappingLoadingRef.current.has(cacheKey)) {
            return;
        }

        loMappingLoadingRef.current.add(cacheKey);

        try {
            const response = await axios.get(`${API_BASE_URL}/api/outcome-mappings/${selectedProgram.programId}/${selectedCourse.courseId}/${outcomeId}`);
            const mappingData = response.data?.mapping || response.data || {};
            const programOutcomeIds = Array.isArray(mappingData.program_outcome_ids)
                ? mappingData.program_outcome_ids
                : [];
            const normalizedIds = programOutcomeIds
                .map((id) => Number(id))
                .filter((num) => !Number.isNaN(num));

            setLoProgramMappings((prev) => {
                if (prev[outcomeId]) {
                    return prev;
                }
                return {
                    ...prev,
                    [outcomeId]: normalizedIds
                };
            });
        } catch (error) {
            console.error('Dersin Öğrenme Çıktısı -> program çıktısı eşlemesi alınamadı:', { outcomeId, error });
        } finally {
            loMappingLoadingRef.current.delete(cacheKey);
        }
    }, [selectedProgram, selectedCourse]);

    // Program çıktılarını yükle (seçilen programa göre)
    useEffect(() => {
        if (selectedProgram && selectedProgram.programId) {
            console.log('Başlangıçta program outcomes yükleniyor...');
            fetchProgramOutcomes(selectedProgram.programId);
        }
    }, [selectedProgram]);

    useEffect(() => {
        setLoProgramMappings({});
        loMappingLoadingRef.current = new Set();
    }, [selectedProgram?.programId, selectedCourse?.courseId]);

    useEffect(() => {
        if (!selectedProgram?.programId || !selectedCourse?.courseId) {
            return;
        }

        const requiredOutcomeIds = new Set();

        (questions || []).forEach((question) => {
            (question?.outcomes || []).forEach((code) => {
                if (typeof code !== 'string') return;
                if (!code.trim().toUpperCase().startsWith('ÖÇ')) return;

                const learningOutcome = learningOutcomeMap[code];
                if (learningOutcome?.id && !loProgramMappings[learningOutcome.id]) {
                    requiredOutcomeIds.add(learningOutcome.id);
                }
            });
        });

        requiredOutcomeIds.forEach((outcomeId) => {
            loadOutcomeMapping(outcomeId);
        });
    }, [questions, selectedProgram, selectedCourse, learningOutcomeMap, loProgramMappings, loadOutcomeMapping]);

    // Akademisyenin derslerini getir
    const fetchAcademicCourses = async (academicId) => {
        if (!academicId) {
            console.log('fetchAcademicCourses: academicId yok');
            return;
        }
        
        console.log('fetchAcademicCourses çağrıldı, academicId:', academicId);
        
        try {
            setLoadingCourses(true);
            const response = await axios.get(API_ENDPOINTS.AKADEMISYEN_ASSIGNED_COURSES_DETAILED(academicId));
            console.log('API yanıtı:', response.data);
            
            if (response.data && response.data.success) {
                setAcademicCourses(response.data.courses);
                console.log('Akademisyen dersleri set edildi:', response.data.courses);
            } else {
                setAcademicCourses([]);
                console.log('API başarısız, dersler boş array olarak set edildi');
            }
        } catch (error) {
            console.error('Akademisyen dersleri yüklenirken hata:', error);
            setAcademicCourses([]);
        } finally {
            setLoadingCourses(false);
        }
    };

    // Outcome mapping kontrolü
    const checkOutcomeMappings = async (programId, courseId) => {
        try {
            console.log('Mapping kontrolü yapılıyor:', { programId, courseId });
            const response = await axios.get(API_ENDPOINTS.CHECK_OUTCOME_MAPPINGS(programId, courseId));
            
            const mappingData = response.data;
            setMappingStatus(mappingData);
            
            if (!mappingData.is_mapped) {
                setMappingErrorMessage(mappingData.message);
                
                // Eğer hiç ders Dersin Öğrenme Çıktısı yoksa farklı mesaj göster
                if (mappingData.total_outcomes === 0) {
                    setMappingErrorMessage('Bu ders için henüz ders Dersin Öğrenme Çıktısı tanımlanmamış. Önce Dersin öğrenme çıktılarını tanımlayın.');
                } else {
                    setMappingErrorMessage(`Bu dersin çıktıları henüz eşleştirilmemiş! ${mappingData.mapped_outcomes}/${mappingData.total_outcomes} Dersin Öğrenme Çıktısı eşleştirilmiş. Soru ekleyebilmek için önce tüm Dersin öğrenme çıktılarını program çıktıları ile eşleştirin.`);
                }
                
            } else {
                setShowMappingError(false);
                setMappingErrorMessage('');
                console.log('Mapping kontrolü başarılı, tüm çıktılar eşleştirilmiş');
            }
            
        } catch (error) {
            console.error('Mapping kontrolü sırasında hata:', error);
            setMappingErrorMessage('Çıktı eşleştirme kontrolü yapılırken bir hata oluştu.');
        }
    };

    // Mapping error'ı kapat
    const closeMappingError = () => {
        setShowMappingError(false);
        setMappingErrorMessage('');
    };

    // Soru ekleme işleminden önce mapping kontrolü
    const validateMappingBeforeQuestionOperation = () => {
        if (!mappingStatus || !mappingStatus.is_mapped) {
            const message =
                mappingErrorMessage ||
                'Bu dersin çıktıları henüz eşleştirilmemiş. Önce Dersin öğrenme çıktılarını program çıktıları ile eşleştirin.';
            toast.error(message, {
                duration: 4000,
                position: 'top-center'
            });
            setShowMappingError(true);
            return false;
        }
        return true;
    };

    // Akademisyen ID'sini sessionStorage'dan al ve dersleri getir
    useEffect(() => {
        console.log('=== localStorage temizleme başladı ===');
        
        // Eski localStorage verilerini temizle
        const oldSelectedCourse = localStorage.getItem('questionOperations_selectedCourse');
        const oldSelectedProgram = localStorage.getItem('questionOperations_selectedProgram');
        
        console.log('Eski selectedCourse:', oldSelectedCourse);
        console.log('Eski selectedProgram:', oldSelectedProgram);
        
        localStorage.removeItem('questionOperations_selectedCourse');
        localStorage.removeItem('questionOperations_selectedProgram');
        localStorage.removeItem('questionOperations_questions');
        localStorage.removeItem('questionOperations_currentIndex');
        localStorage.removeItem('questionOperations_examType');
        localStorage.removeItem('questionOperations_fileInfo');
        localStorage.removeItem('questionOperations_activeTab');
        
        // State'leri sıfırla
        setSelectedCourse(null);
        setSelectedProgram(null);
        setQuestions([]);
        setCurrentQuestionIndex(0);
        setSelectedExamType('');
        setUploadedFile(null);
        setFileInfo(null);
        setActiveTab('upload');
        setLearningOutcomes([]);
        setProgramOutcomes([]);
        
        console.log('=== localStorage temizleme tamamlandı ===');
        
        // Önce sessionStorage'dan akademisyen ID'sini al
        const sessionAcademicId = sessionStorage.getItem('akademisyen_id');
        console.log('SessionStorage akademisyen_id:', sessionAcademicId);
        
        if (sessionAcademicId) {
            setAcademicId(sessionAcademicId);
            fetchAcademicCourses(sessionAcademicId);
        } else {
            // Eğer sessionStorage'da yoksa, localStorage'dan kontrol et
            const savedAcademicId = localStorage.getItem('academicId') || localStorage.getItem('questionOperations_academicId');
            console.log('LocalStorage academicId:', savedAcademicId);
            
            if (savedAcademicId) {
                setAcademicId(savedAcademicId);
                fetchAcademicCourses(savedAcademicId);
            } else {
                // Eğer localStorage'da da yoksa, giriş yapılmış akademisyen bilgisini kontrol et
                const loggedInAcademic = JSON.parse(localStorage.getItem('academicData') || '{}');
                console.log('LocalStorage academicData:', loggedInAcademic);
                if (loggedInAcademic.id) {
                    setAcademicId(loggedInAcademic.id);
                    fetchAcademicCourses(loggedInAcademic.id);
                }
            }
        }
        
        // localStorage'dan yüklenen ders bilgisi varsa Dersin öğrenme çıktılarını yükle
        const savedCourse = localStorage.getItem('questionOperations_selectedCourse');
        if (savedCourse) {
            try {
                const parsedCourse = JSON.parse(savedCourse);
                if (parsedCourse && (parsedCourse.courseId || parsedCourse.id)) {
                    console.log('LocalStorage\'dan yüklenen ders:', parsedCourse);
                    // Ders bilgisi yüklendikten sonra Dersin öğrenme çıktılarını yükle
                    setTimeout(() => {
                        fetchLearningOutcomes(parsedCourse.courseId || parsedCourse.id);
                    }, 1000); // 1 saniye bekle, akademisyen dersleri yüklensin
                }
            } catch (error) {
                console.error('LocalStorage\'dan ders bilgisi parse edilirken hata:', error);
            }
        }
        
        // Başlangıçta soruları yükleme - sadece kullanıcı seçim yaptıktan sonra sorular gösterilecek
        // localStorage'dan soruları temizle ve başlangıçta boş bırak
        setQuestions([]);
        localStorage.removeItem('questionOperations_questions');
        setActiveTab('upload'); // Başlangıçta upload tabında başla
    }, []);

    // Seçilen derse göre Dersin öğrenme çıktılarını güncelle
    useEffect(() => {
        if (selectedCourse) {
            console.log('Seçilen ders değişti, learning outcomes yükleniyor:', selectedCourse.id);
            fetchLearningOutcomes(selectedCourse.id);
        }
    }, [selectedCourse]);

    // State değişikliklerini localStorage'a kaydet
    useEffect(() => {
        localStorage.setItem('questionOperations_questions', JSON.stringify(questions));
    }, [questions]);

    useEffect(() => {
        localStorage.setItem('questionOperations_currentIndex', currentQuestionIndex.toString());
    }, [currentQuestionIndex]);

    useEffect(() => {
        localStorage.setItem('questionOperations_examType', selectedExamType);
        
        // Sınav türü değiştiğinde, tüm seçimler tamamlandıysa soruları kontrol et
        if (selectedExamType && selectedProgram && selectedCourse) {
            console.log('useEffect: Sınav türü değişti, tüm seçimler tamamlandı, sorular kontrol ediliyor:', selectedExamType);
            // Kısa bir gecikme ile soruları kontrol et
            setTimeout(() => {
                checkAndLoadExistingQuestions();
            }, 300);
        } else {
            // Eğer seçimler eksikse soruları temizle
            setQuestions([]);
            setActiveTab('upload');
        }
    }, [selectedExamType]);

    useEffect(() => {
        localStorage.setItem('questionOperations_activeTab', activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (fileInfo) {
            localStorage.setItem('questionOperations_fileInfo', JSON.stringify(fileInfo));
        }
    }, [fileInfo]);

    // Yeni state'ler için localStorage kaydetme
    useEffect(() => {
        if (selectedProgram) {
            localStorage.setItem('questionOperations_selectedProgram', JSON.stringify(selectedProgram));
            
            // Program değiştiğinde, tüm seçimler tamamlandıysa soruları kontrol et
            if (selectedExamType && selectedCourse) {
                console.log('useEffect: Program değişti, tüm seçimler tamamlandı, sorular kontrol ediliyor');
                setTimeout(() => {
                    checkAndLoadExistingQuestions();
                }, 300);
            } else {
                // Eğer seçimler eksikse soruları temizle
                setQuestions([]);
                setActiveTab('upload');
            }
        }
    }, [selectedProgram]);

    useEffect(() => {
        if (selectedCourse) {
            localStorage.setItem('questionOperations_selectedCourse', JSON.stringify(selectedCourse));
            
            // Ders değiştiğinde, tüm seçimler tamamlandıysa soruları kontrol et
            if (selectedExamType && selectedProgram) {
                console.log('useEffect: Ders değişti, tüm seçimler tamamlandı, sorular kontrol ediliyor');
                setTimeout(() => {
                    checkAndLoadExistingQuestions();
                }, 300);
            } else {
                // Eğer seçimler eksikse soruları temizle
                setQuestions([]);
                setActiveTab('upload');
            }
        }
    }, [selectedCourse]);

    useEffect(() => {
        if (academicId) {
            localStorage.setItem('questionOperations_academicId', academicId);
        }
    }, [academicId]);

    
    // Program ve ders seçildiğinde mapping status'u yenile
    useEffect(() => {
        if (selectedProgram && selectedCourse) {
            console.log('🔄 Program ve ders seçildi, mapping status yenileniyor...');
            refreshMappingStatus();
        }
    }, [selectedProgram, selectedCourse]);


    // Tab switching
    const switchTab = (tabName) => {
        console.log('switchTab çağrıldı, tabName:', tabName);
        setActiveTab(tabName);
        
        // localStorage'ı güncelle
        localStorage.setItem('questionOperations_activeTab', tabName);
    };

    // File upload handling
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setUploadedFile(file);
            const fileInfoData = {
                name: file.name,
                size: file.size,
                type: file.type
            };
            setFileInfo(fileInfoData);
            
            // localStorage'ı güncelle
            localStorage.setItem('questionOperations_fileInfo', JSON.stringify(fileInfoData));
        }
    };

    const updateProcessButton = () => {
        return uploadedFile && selectedExamType && selectedProgram && selectedCourse && academicCourses.length > 0;
    };

    const handleExamTypeChange = (examType) => {
        console.log('Sınav türü değişiyor:', examType);
        
        // Sınav türü değiştiğinde mevcut soruları hemen temizle
        setQuestions([]);
        setCurrentQuestionIndex(0);
        localStorage.removeItem('questionOperations_questions');
        localStorage.setItem('questionOperations_currentIndex', '0');
        
        // Sınav türünü normalize ederek güncelle ve kalıcılaştır
        const norm = String(examType || '').trim().toLowerCase();
        let canon = norm;
        if (['final','finaller'].includes(norm)) canon = 'final';
        else if (['vize','midterm'].includes(norm)) canon = 'vize';
        else if (['but','bütünleme','butunleme','resit'].includes(norm)) canon = 'but';
        
        console.log('Normalized exam type:', canon);
        setSelectedExamType(canon);
        localStorage.setItem('questionOperations_examType', canon);
        
        // Eğer program ve ders seçiliyse, yeni sınav türü için soruları kontrol et
        if (canon && selectedProgram && selectedCourse) {
            console.log('Yeni sınav türü için sorular kontrol ediliyor:', canon);
            // Kısa bir gecikme ile soruları kontrol et (state güncellemesi için)
            setTimeout(() => {
                checkAndLoadExistingQuestions();
            }, 100);
        } else {
            console.log('Sınav türü değişti ama program/ders eksik:', { canon, selectedProgram, selectedCourse });
        }
    };

    // Program seçme işleyicisi
    const handleProgramChange = async (program) => {
        if (program && program.programId) {
            // programId'nin geçerli bir sayı olduğundan emin ol
            const numericProgramId = parseInt(program.programId);
            if (isNaN(numericProgramId) || numericProgramId <= 0) {
                console.error('handleProgramChange: Geçersiz programId:', program.programId);
                return;
            }
            
            setSelectedProgram({
                programId: numericProgramId,
                programName: program.programName
            });
            setSelectedCourse(null); // Program değiştiğinde ders seçimini sıfırla
            setLearningOutcomes([]); // Program değiştiğinde Dersin öğrenme çıktılarını temizle
            // Program değiştiğinde soruları da temizle
            setQuestions([]);
            setCurrentQuestionIndex(0);
            localStorage.removeItem('questionOperations_questions');
            localStorage.removeItem('questionOperations_currentIndex');
            localStorage.removeItem('questionOperations_selectedCourse');
            sessionStorage.removeItem('current_course_id');
        } else {
            setSelectedProgram(null);
            setSelectedCourse(null);
            setLearningOutcomes([]); // Program kaldırıldığında Dersin öğrenme çıktılarını temizle
            setQuestions([]);
            setCurrentQuestionIndex(0);
            localStorage.removeItem('questionOperations_questions');
            localStorage.removeItem('questionOperations_currentIndex');
            localStorage.removeItem('questionOperations_selectedCourse');
            sessionStorage.removeItem('current_course_id');
        }
    };

    // Ders seçme işleyicisi
    const handleCourseChange = async (course) => {
        console.log('handleCourseChange başladı, gelen course:', course);
        
        if (course && course.courseId) {
            console.log('course.courseId:', course.courseId, 'tipi:', typeof course.courseId);
            
            // courseId'nin geçerli bir sayı olduğundan emin ol
            const numericCourseId = parseInt(course.courseId);
            console.log('parseInt sonucu:', numericCourseId, 'isNaN:', isNaN(numericCourseId));
            
            if (isNaN(numericCourseId) || numericCourseId <= 0) {
                console.error('handleCourseChange: Geçersiz courseId:', course.courseId);
                return;
            }
            
            userSelectedCourseRef.current = true;
            setSelectedCourse({
                courseId: numericCourseId,
                courseCode: course.courseCode,
                courseName: course.courseName
            });
            
            // Ders seçildiğinde outcome mapping kontrolü yap
            // Program ID'yi ders bilgisinden al
            const programId = course.programId || (selectedProgram ? selectedProgram.programId : null);
            console.log('🎯 Program ID kontrolü:', {
                course_programId: course.programId,
                selectedProgram_programId: selectedProgram ? selectedProgram.programId : null,
                final_programId: programId,
                courseId: numericCourseId
            });
            
            if (programId) {
                console.log('✅ Eşleştirme kontrolü yapılıyor:', { programId, courseId: numericCourseId });
                await checkOutcomeMappings(programId, numericCourseId);
            } else {
                console.warn('❌ Program ID bulunamadı, eşleştirme kontrolü yapılamıyor');
            }
            
            // Ders seçildiğinde Dersin öğrenme çıktılarını yükle
            fetchLearningOutcomes(numericCourseId);
            
            // Session storage'a mevcut ders ID'sini kaydet
            sessionStorage.setItem('current_course_id', course.courseId.toString());
            
            // Bu ders için kaydedilmiş soruları kontrol et ve yükle
            if (selectedProgram && selectedExamType) {
                // Kısa bir gecikme ile soruları kontrol et (state güncellemesi için)
                setTimeout(() => {
                    checkAndLoadExistingQuestions();
                }, 100);
            } else {
                // Program veya sınav türü seçilmemişse soruları temizle
                setQuestions([]);
                setCurrentQuestionIndex(0);
                localStorage.removeItem('questionOperations_questions');
                localStorage.removeItem('questionOperations_currentIndex');
                localStorage.setItem('questionOperations_currentIndex', '0');
            }
            
            // Eğer sorular tabındaysa, soruları göster
            if (activeTab === 'questions' && questions.length > 0) {
                // Sorular zaten yüklü, bir şey yapma
            }
        } else {
            userSelectedCourseRef.current = false;
            setSelectedCourse(null);
            setLearningOutcomes([]); // Ders seçimi kaldırıldığında Dersin öğrenme çıktılarını temizle
            setQuestions([]); // Soruları da temizle
            setCurrentQuestionIndex(0);
            localStorage.removeItem('questionOperations_questions');
            localStorage.removeItem('questionOperations_currentIndex');
            sessionStorage.removeItem('current_course_id');
        }
    };

    const processExcelFile = async () => {
        if (!uploadedFile || !selectedExamType) {
            alert('Lütfen bir dosya ve sınav türü seçin');
            return;
        }

        if (!selectedProgram) {
            alert('Lütfen bir program seçin');
            return;
        }

        if (!selectedCourse) {
            alert('Lütfen bir ders seçin');
            return;
        }

        // Mapping kontrolü yap
        if (!validateMappingBeforeQuestionOperation()) {
            return;
        }

        setIsUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('examType', selectedExamType);
            formData.append('programId', selectedProgram.programId);
            formData.append('courseId', selectedCourse.courseId);
            formData.append('bookletCount', String(bookletCount || 2));

            // Debug: FormData içeriğini kontrol et
            console.log('FormData içeriği:');
            for (let [key, value] of formData.entries()) {
                console.log(`${key}: ${value}`);
            }
            console.log('Seçilen ders:', selectedCourse);
            console.log('Seçilen program:', selectedProgram);
            console.log('Seçilen sınav türü:', selectedExamType);

            const response = await axios.post(API_ENDPOINTS.UPLOAD_QUESTIONS_EXCEL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const questionsToSave = response.data.questions;
                
                // Toplam puan kontrolü
                const totalScore = questionsToSave.reduce((sum, q) => sum + (parseInt(q.score) || 0), 0);
                if (totalScore !== 100) {
                    setIsUploading(false);
                    toast.error(
                        `Toplam soru puanı 100 olmalıdır!\n\nMevcut toplam: ${totalScore} puan\n${totalScore < 100 ? `Eksik: ${100 - totalScore} puan` : `Fazla: ${totalScore - 100} puan`}`,
                        { duration: 6000 }
                    );
                    return;
                }
                
                // Soruları veritabanına otomatik kaydet
                console.log('Excel yüklendi, sorular veritabanına kaydediliyor...');
                const savedQuestions = [];
                let successCount = 0;
                let errorCount = 0;

                for (const question of questionsToSave) {
                    try {
                        // Her soruyu veritabanına kaydet
                        const saveResponse = await axios.post(API_ENDPOINTS.SAVE_SINGLE_QUESTION, {
                            question: {
                                text: question.text,
                                score: question.score,
                                outcomes: [], // Başlangıçta boş outcomes
                                row_number: question.row_number
                            },
                            examType: selectedExamType,
                            programId: selectedProgram ? selectedProgram.programId : 1,
                            courseId: selectedCourse ? selectedCourse.courseId : 1
                        });

                        if (saveResponse.data.success) {
                            // Kaydedilen soruyu güncelle
                            const savedQuestion = {
                                ...question,
                                id: saveResponse.data.question_id,
                                outcomes: [],
                                saved: true
                            };
                            savedQuestions.push(savedQuestion);
                            successCount++;
                        } else {
                            console.error('Soru kaydedilemedi:', saveResponse.data.error);
                            errorCount++;
                        }
                    } catch (error) {
                        console.error('Soru kaydedilirken hata:', error);
                        errorCount++;
                    }
                }

                // Sonuçları göster
                if (successCount > 0) {
                    // Kaydedilen soruları state'e set et
                    setQuestions(savedQuestions);
                    setActiveTab('questions');
                    
                    // localStorage'ı güncelle
                    localStorage.setItem('questionOperations_questions', JSON.stringify(savedQuestions));
                    localStorage.setItem('questionOperations_activeTab', 'questions');
                    localStorage.setItem('questionOperations_currentIndex', '0');
                    localStorage.removeItem('questionOperations_fileInfo');
                    
                    // Session storage'a mevcut ders ID'sini kaydet
                    if (selectedCourse && selectedCourse.courseId) {
                        sessionStorage.setItem('current_course_id', selectedCourse.courseId.toString());
                    }
                    
                    // Reset file input
                    const fileInput = document.getElementById('excel-file');
                    if (fileInput) fileInput.value = '';
                    setUploadedFile(null);
                    setFileInfo(null);
                    
                    // 1'den fazla kitapçık varsa otomatik eşleştirme yap
                    if (bookletCount > 1) {
                        try {
                            toast.loading('Kitapçık eşleştirmesi oluşturuluyor...', { id: 'auto-booklet-mapping' });
                            
                            const mappingResponse = await axios.post(API_ENDPOINTS.GENERATE_BOOKLET_MAPPINGS, {
                                programId: selectedProgram.programId,
                                courseId: selectedCourse.courseId,
                                examType: selectedExamType,
                                bookletCount: bookletCount
                            });
                            
                            if (mappingResponse.data.success) {
                                // Soruları yeniden yükle (booklet_map ile)
                                await checkAndLoadExistingQuestions();
                                toast.dismiss('auto-booklet-mapping');
                                toast.success(`${successCount} soru kaydedildi ve kitapçık eşleştirmesi oluşturuldu!`);
                            } else {
                                toast.dismiss('auto-booklet-mapping');
                                toast.success(`${successCount} soru kaydedildi!${errorCount > 0 ? ` (${errorCount} hata)` : ''}`);
                            }
                        } catch (mappingError) {
                            toast.dismiss('auto-booklet-mapping');
                            console.error('Kitapçık eşleştirme hatası:', mappingError);
                            toast.success(`${successCount} soru kaydedildi!${errorCount > 0 ? ` (${errorCount} hata)` : ''}`);
                        }
                    } else {
                        toast.success(`${successCount} soru başarıyla kaydedildi!${errorCount > 0 ? ` (${errorCount} hata)` : ''}`);
                    }
                } else {
                    toast.error('Hiç soru kaydedilemedi!');
                }
                
            } else {
                alert('Dosya işlenirken bir hata oluştu: ' + response.data.error);
            }
        } catch (error) {
            alert('Dosya yüklenirken bir hata oluştu: ' + error.message);
        } finally {
            setIsUploading(false);
        }
    };

    const updateQuestionCount = () => {
        return questions.length;
    };

    // Soru durumunu dinamik olarak belirle
    const getQuestionStatus = (question) => {
        const hasOutcomes = question.outcomes && question.outcomes.length > 0;
        const learningOutcomes = question.outcomes ? question.outcomes.filter(o => o.startsWith('ÖÇ')) : [];
        const hasLearningOutcomes = learningOutcomes.length > 0;
        
        // Durum belirleme mantığı
        if (question.saved && hasLearningOutcomes) {
            return { text: 'Tamamlandı', className: 'badge-success' };
        } else if (question.saved && !hasLearningOutcomes) {
            return { text: 'Kaydedildi', className: 'badge-warning' };
        } else if (!question.saved && hasLearningOutcomes) {
            return { text: 'Eşleştirildi', className: 'badge-info' };
        } else {
            return { text: 'Eşleştirme bekleniyor', className: 'badge-danger' };
        }
    };

    const getQuestionOutcomeDisplayData = useCallback((question) => {
        if (!question || !Array.isArray(question.outcomes)) {
            return [];
        }

        const learningCodes = question.outcomes
            .filter(code => typeof code === 'string' && code.trim().toUpperCase().startsWith('ÖÇ'))
            .sort((a, b) => {
                const aNum = parseInt(a.replace(/\D/g, ''), 10);
                const bNum = parseInt(b.replace(/\D/g, ''), 10);
                if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
                    return a.localeCompare(b, 'tr', { numeric: true });
                }
                return aNum - bNum;
            });

        return learningCodes.map((code) => {
            const learningOutcome = learningOutcomeMap[code];
            const outcomeId = learningOutcome?.id;
            const programIds = outcomeId ? (loProgramMappings[outcomeId] || []) : [];
            const programOutcomesData = programIds
                .map((poId) => {
                    const byId = programOutcomeMap[String(poId)];
                    const byCode = programOutcomeMap[`PÇ${poId}`];
                    const entry = byId || byCode || null;
                    if (!entry && !poId) return null;
                    return {
                        code: (() => { const c = entry?.code || (poId ? `PÇ${poId}` : ''); return c && !String(c).startsWith('PÇ') ? `PÇ${c}` : c; })(),
                        description: entry?.description || ''
                    };
                })
                .filter(Boolean);

            return {
                code,
                description: learningOutcome?.description || '',
                programOutcomes: programOutcomesData
            };
        });
    }, [learningOutcomeMap, loProgramMappings, programOutcomeMap]);

    const renderOutcomeSummary = (question) => {
        const learningPairs = getQuestionOutcomeDisplayData(question);

        if (learningPairs.length === 0) {
            return <span className="text-muted no-outcomes-text">Henüz seçilmedi</span>;
        }

        return (
            <div className="outcome-mapping-summary">
                {learningPairs.map((pair) => (
                    <div key={pair.code} className="outcome-mapping-line">
                        <span
                            className="badge badge-learning"
                            title={pair.description || 'Açıklama bulunamadı'}
                        >
                            {pair.code}
                        </span>
                        <span className="outcome-arrow">→</span>
                        <div className="program-badge-group">
                            {pair.programOutcomes.length > 0 ? (
                                pair.programOutcomes.map((po) => (
                                    <span
                                        key={`${pair.code}-${po.code}`}
                                        className="badge badge-program"
                                        title={po.description || 'Açıklama bulunamadı'}
                                    >
                                        {po.code}
                                    </span>
                                ))
                            ) : (
                                <span className="badge badge-outline" title="Bu ÖÇ için eşleştirilmiş program çıktısı yok">
                                    PÇ yok
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderQuestionsList = () => {
        if (questions.length === 0) {
            return (
                <div className="empty-state">
                    <i className="fas fa-file-excel"></i>
                    <p>Henüz soru yüklenmedi</p>
                    <p className="small">Excel dosyası yükleyerek başlayın</p>
                </div>
            );
        }

        // Takas işlemini gerçekleştir
        const executeSwap = async () => {
            if (!swapConfirm) return;
            
            const { question, questionIndex, conflictingQuestion, conflictingANum, letter, idx, newValue, oldValue, bookletMap } = swapConfirm;
            
            try {
                // 1. Çakışan sorunun booklet_map'ini güncelle
                const conflictingNewMap = [...(conflictingQuestion.booklet_map || [])];
                while (conflictingNewMap.length <= idx + 1) {
                    conflictingNewMap.push(0);
                }
                conflictingNewMap[idx + 1] = oldValue;
                
                await axios.put(API_ENDPOINTS.UPDATE_QUESTION, {
                    id: conflictingQuestion.id,
                    text: conflictingQuestion.text,
                    score: conflictingQuestion.score,
                    outcomes: conflictingQuestion.outcomes || [],
                    bookletMap: conflictingNewMap
                });
                
                // 2. Bu sorunun booklet_map'ini güncelle
                const newBookletMap = [...bookletMap];
                while (newBookletMap.length <= idx + 1) {
                    newBookletMap.push(0);
                }
                newBookletMap[idx + 1] = newValue;
                
                await axios.put(API_ENDPOINTS.UPDATE_QUESTION, {
                    id: question.id,
                    text: question.text,
                    score: question.score,
                    outcomes: question.outcomes || [],
                    bookletMap: newBookletMap
                });
                
                // Local state'i güncelle
                setQuestions(prev => prev.map(q => {
                    if (q.id === question.id) {
                        return { ...q, booklet_map: newBookletMap };
                    }
                    if (q.id === conflictingQuestion.id) {
                        return { ...q, booklet_map: conflictingNewMap };
                    }
                    return q;
                }));
                
                toast.success(`Eşleştirme değiştirildi: A${questionIndex + 1} ↔ A${conflictingANum}`);
                setSwapConfirm(null);
            } catch (error) {
                toast.error('Eşleştirme değiştirilirken hata oluştu');
            }
        };

        return (
            <div className="questions-list">
                {/* Toplu Silme Onay Modalı */}
                {deleteConfirm && (
                    <div className="delete-confirm-overlay" onClick={() => !isDeleting && setDeleteConfirm(false)}>
                        <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="delete-confirm-header">
                                <i className="fas fa-exclamation-triangle"></i>
                                <h3>Soruları Sil</h3>
                            </div>
                            <div className="delete-confirm-body">
                                <p className="delete-confirm-warning">
                                    <i className="fas fa-exclamation-circle"></i>
                                    Bu işlem geri alınamaz!
                                </p>
                                <p className="delete-confirm-message">
                                    <strong>{questions.length} soru</strong> silinecek.
                                    <br /><br />
                                    Bu işlem, seçili ders ve sınav türüne ait tüm soruları kalıcı olarak silecektir.
                                </p>
                            </div>
                            <div className="delete-confirm-actions">
                                <button 
                                    className="delete-btn delete-btn-cancel"
                                    onClick={() => setDeleteConfirm(false)}
                                    disabled={isDeleting}
                                >
                                    <i className="fas fa-times"></i>
                                    Vazgeç
                                </button>
                                <button 
                                    className="delete-btn delete-btn-confirm"
                                    onClick={async () => {
                                        if (!selectedProgram || !selectedCourse || !selectedExamType) {
                                            toast.error('Program, ders ve sınav türü seçili olmalı');
                                            return;
                                        }
                                        
                                        setIsDeleting(true);
                                        
                                        try {
                                            const response = await axios.delete(API_ENDPOINTS.BULK_DELETE_QUESTIONS, {
                                                data: {
                                                    programId: selectedProgram.programId,
                                                    courseId: selectedCourse.courseId,
                                                    examType: selectedExamType
                                                }
                                            });
                                            
                                            if (response.data.success) {
                                                toast.success(response.data.message);
                                                
                                                // State'leri temizle
                                                setQuestions([]);
                                                setCurrentQuestionIndex(0);
                                                localStorage.removeItem('questionOperations_questions');
                                                localStorage.setItem('questionOperations_currentIndex', '0');
                                                
                                                // Upload tabına dön
                                                setActiveTab('upload');
                                                setDeleteConfirm(false);
                                            } else {
                                                toast.error(response.data.error || 'Silme işlemi başarısız');
                                            }
                                        } catch (error) {
                                            toast.error('Silme hatası: ' + (error.response?.data?.error || error.message));
                                        } finally {
                                            setIsDeleting(false);
                                        }
                                    }}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Siliniyor...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-trash-alt"></i>
                                            Evet, Sil
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Eşleştirme Değiştir Modalı */}
                {swapConfirm && (
                    <div className="swap-confirm-overlay" onClick={() => setSwapConfirm(null)}>
                        <div className="swap-confirm-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="swap-confirm-header">
                                <i className="fas fa-sync-alt"></i>
                                <h3>Eşleştirmeyi Değiştir</h3>
                            </div>
                            <div className="swap-confirm-body">
                                <p className="swap-confirm-message">
                                    <strong>{swapConfirm.letter}{swapConfirm.newValue}</strong> şu anda 
                                    <strong> A{swapConfirm.conflictingANum}</strong> sorusuyla eşleşik.
                                </p>
                                <p className="swap-confirm-question">Eşleştirmeleri değiştirmek ister misiniz?</p>
                                <div className="swap-preview">
                                    <div className="swap-preview-item swap-from">
                                        <span className="swap-badge swap-badge-a">A{swapConfirm.questionIndex + 1}</span>
                                        <i className="fas fa-long-arrow-alt-right"></i>
                                        <span className="swap-badge swap-badge-b">{swapConfirm.letter}{swapConfirm.newValue}</span>
                                    </div>
                                    <div className="swap-preview-item swap-to">
                                        <span className="swap-badge swap-badge-a">A{swapConfirm.conflictingANum}</span>
                                        <i className="fas fa-long-arrow-alt-right"></i>
                                        <span className="swap-badge swap-badge-b">{swapConfirm.letter}{swapConfirm.oldValue}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="swap-confirm-actions">
                                <button 
                                    className="swap-btn swap-btn-cancel"
                                    onClick={() => setSwapConfirm(null)}
                                >
                                    <i className="fas fa-times"></i>
                                    Vazgeç
                                </button>
                                <button 
                                    className="swap-btn swap-btn-confirm"
                                    onClick={executeSwap}
                                >
                                    <i className="fas fa-check"></i>
                                    Değiştir
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Uyarı Mesajı */}
                {(!selectedExamType || !selectedProgram || !selectedCourse) && (
                    <div className="warning-message">
                        <i className="fas fa-exclamation-triangle"></i>
                        <div>
                            <strong>Dikkat:</strong> Sınav türü, program veya ders seçimi yapılmamış. 
                            Bu sorular önceki bir oturumdan kalmış olabilir.
                        </div>
                    </div>
                )}
                
                <div className="questions-header">
                    {/* Üst Satır: Başlık ve Puan Barı */}
                    <div className="questions-header-top">
                        <div>
                            <h3>Yüklenen Sorular</h3>
                            <p className="subtitle">Soruları görüntüleyin ve Dersin öğrenme çıktıları ile eşleştirin</p>
                        </div>
                        {/* Toplam Puan Progress Bar - Sağ Köşede */}
                        {questions.length > 0 && (() => {
                            const totalScore = questions.reduce((sum, q) => sum + (parseInt(q.score) || 0), 0);
                            const percentage = Math.min((totalScore / 100) * 100, 100);
                            const barColor = totalScore === 100 ? '#10b981' : totalScore < 100 ? '#f59e0b' : '#ef4444';
                            
                            return (
                                <div className="score-progress-inline">
                                    <span className="score-progress-label-inline">Toplam Puan:</span>
                                    <span className="score-progress-text" style={{ color: barColor }}>
                                        {totalScore}/100
                                    </span>
                                    <div className="score-progress-bar-inline-bg">
                                        <div 
                                            className="score-progress-bar-inline-fill"
                                            style={{ 
                                                width: `${percentage}%`,
                                                backgroundColor: barColor
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    
                    {/* Alt Satır: Butonlar */}
                    <div className="questions-header-actions">
                        <button
                            className={`btn btn-success bulk-ai-button ${bulkAiLoading ? 'loading' : ''}`}
                            onClick={async () => {
                                if (!selectedCourse || !selectedCourse.courseId) {
                                    toast.error('Lütfen bir ders seçin');
                                    return;
                                }
                                if (questions.length === 0) {
                                    toast.error('Eşleştirilecek soru bulunamadı');
                                    return;
                                }
                                if (learningOutcomes.length === 0) {
                                    toast.error('Dersin öğrenme çıktıları yüklenmedi');
                                    return;
                                }
                                
                                setBulkAiLoading(true);
                                setBulkAiProgress(0);
                                
                                try {
                                    // 1. Adım: Tüm sorular için AI önerileri al
                                    toast.loading('Tüm sorular için Yapay Zeka analizi yapılıyor...', { id: 'bulk-ai-apply' });
                                    
                                    let successCount = 0;
                                    let errorCount = 0;
                                    let appliedCount = 0;
                                    let skippedCount = 0;
                                    
                                    // Her soru için sırayla: AI analizi yap, önerileri al, uygula ve kaydet
                                    // State'ten güncel questions listesini al
                                    let currentQuestionsList;
                                    await new Promise(resolve => {
                                        setQuestions(prev => {
                                            currentQuestionsList = [...prev].filter(q => q && q.id); // Sadece geçerli soruları al
                                            resolve();
                                            return prev;
                                        });
                                    });
                                    
                                    const totalQuestions = currentQuestionsList.length;
                                    
                                    console.log(`🔍 DEBUG: Toplam ${totalQuestions} soru işlenecek`);
                                    console.log('🔍 DEBUG: İşlenecek sorular:', currentQuestionsList.map((q, idx) => `[${idx}] ID: ${q.id}, Metin: ${q.text?.substring(0, 30)}...`));
                                    
                                    if (totalQuestions === 0) {
                                        toast.error('İşlenecek soru bulunamadı');
                                        setBulkAiLoading(false);
                                        return;
                                    }
                                    
                                    for (let i = 0; i < currentQuestionsList.length; i++) {
                                        const question = currentQuestionsList[i];
                                        
                                        if (!question) {
                                            console.warn(`Soru ${i + 1} (index ${i}) bulunamadı`);
                                            errorCount++;
                                            continue;
                                        }
                                        
                                        if (!question.id) {
                                            console.warn(`Soru ${i + 1} (index ${i}) ID'si yok:`, question);
                                            errorCount++;
                                            continue;
                                        }
                                        
                                        console.log(`🔍 [${i + 1}/${totalQuestions}] İşleniyor: Soru ID: ${question.id}, Metin: ${question.text?.substring(0, 50)}...`);
                                        
                                        // Progress güncelle (i+1 kullan çünkü 1'den başlıyor)
                                        setBulkAiProgress(Math.round(((i + 1) / totalQuestions) * 100));
                                        
                                        try {
                                            // 1. AI analizi yap
                                            console.log(`🔍 Soru ${i + 1} (ID: ${question.id}) için Yapay Zeka analizi başlatılıyor...`);
                                            const response = await axios.post(API_ENDPOINTS.AI_MATCH_QUESTION, {
                                                question_text: question.text,
                                                course_id: selectedCourse.courseId
                                            });
                                            console.log(`🔍 Soru ${i + 1} (ID: ${question.id}) için Yapay Zeka analizi tamamlandı`);
                                            
                                            if (response.data.success && response.data.matches) {
                                                const matches = response.data.matches;
                                                
                                                console.log(`Soru ${i + 1} için ${matches.length} Yapay Zeka önerisi alındı`);
                                                
                                                // AI önerilerini state'e kaydet
                                                setQuestionAiMatches(prev => ({
                                                    ...prev,
                                                    [question.id]: matches
                                                }));
                                                
                                                successCount++;
                                                
                                                // 2. En yüksek uyum oranına sahip önerileri uygula (%50 ve üzeri)
                                                const highConfidenceMatches = matches
                                                    .filter(m => (m.match_percentage || 0) >= 50)
                                                    .sort((a, b) => (b.match_percentage || 0) - (a.match_percentage || 0));
                                                
                                                if (highConfidenceMatches.length === 0) {
                                                    console.log(`⚠️ Soru ${i + 1} (ID: ${question.id}) için yüksek uyum oranlı öneri yok (toplam ${matches.length} öneri var, hepsi %50'nin altında)`);
                                                    skippedCount++;
                                                    // continue ile atlama, bir sonraki soruya geç
                                                    continue;
                                                }
                                                
                                                // Her soru için en yüksek 3 öneriyi uygula
                                                const topMatches = highConfidenceMatches.slice(0, 3);
                                                
                                                console.log(`Soru ${i + 1} için ${topMatches.length} öneri uygulanıyor`);
                                                
                                                // Her öneriyi uygula
                                                for (const match of topMatches) {
                                                    const outcome = learningOutcomes.find(o => o.code === match.code);
                                                    if (outcome) {
                                                        // Güncel soru bilgisini al (state'ten)
                                                        let currentQuestionState;
                                                        await new Promise(resolve => {
                                                            setQuestions(prev => {
                                                                currentQuestionState = prev.find(q => q.id === question.id) || question;
                                                                resolve();
                                                                return prev;
                                                            });
                                                        });
                                                        
                                                        const isAlreadySelected = currentQuestionState.outcomes && currentQuestionState.outcomes.includes(outcome.code);
                                                        if (!isAlreadySelected) {
                                                            // Öneriyi uygula ve kaydet
                                                            console.log(`Soru ${i + 1} için ${match.code} eklendi`);
                                                            await updateQuestionOutcomes(outcome.id, true, question.id);
                                                            appliedCount++;
                                                        } else {
                                                            console.log(`Soru ${i + 1} için ${match.code} zaten seçili`);
                                                        }
                                                    }
                                                }
                                            } else {
                                                console.warn(`Soru ${i + 1} için Yapay Zeka önerisi alınamadı`);
                                                errorCount++;
                                            }
                                        } catch (error) {
                                            console.error(`Soru ${i + 1} işlenirken hata:`, error);
                                            errorCount++;
                                        }
                                    }
                                    
                                    setBulkAiProgress(100);
                                    
                                    toast.dismiss('bulk-ai-apply');
                                    toast.success(`${appliedCount} Yapay Zeka önerisi uygulandı! ${successCount} soru analiz edildi. ${skippedCount > 0 ? `(${skippedCount} soru atlandı)` : ''} ${errorCount > 0 ? `(${errorCount} hata)` : ''}`);
                                } catch (error) {
                                    toast.dismiss('bulk-ai-apply');
                                    toast.error('İşlem sırasında bir hata oluştu');
                                } finally {
                                    setBulkAiLoading(false);
                                    setTimeout(() => setBulkAiProgress(0), 500);
                                }
                            }}
                            disabled={bulkAiLoading || !selectedCourse || questions.length === 0 || learningOutcomes.length === 0}
                        >
                            <span className="button-content">
                                <i className="fas fa-magic"></i> Yapay Zeka Öner ve Uygula
                            </span>
                            {bulkAiLoading && (
                                <span 
                                    className="button-loading-bar"
                                    style={{ height: `${bulkAiProgress}%` }}
                                ></span>
                            )}
                        </button>
                        
                        {/* Kitapçık Bazında Soruları İndir Butonu */}
                        {bookletCount > 1 && questions.length > 0 && (
                            <button
                                className="btn btn-primary"
                                onClick={async () => {
                                    if (!selectedProgram || !selectedCourse || !selectedExamType) {
                                        toast.error('Program, ders ve sınav türü seçili olmalı');
                                        return;
                                    }
                                    
                                    // Eşleştirme kontrolü
                                    const hasBookletMap = questions.some(q => q.booklet_map && q.booklet_map.length > 0);
                                    if (!hasBookletMap) {
                                        toast.error('Önce kitapçık eşleştirmesi yapılmalı. Lütfen soruları yeniden yükleyin.');
                                        return;
                                    }
                                    
                                    try {
                                        toast.loading('Kitapçık dosyaları hazırlanıyor...', { id: 'download-booklets' });
                                        
                                        const response = await axios.post(API_ENDPOINTS.GENERATE_BOOKLETS, {
                                            programId: selectedProgram.programId,
                                            courseId: selectedCourse.courseId,
                                            examType: selectedExamType,
                                            bookletCount: bookletCount,
                                            useExistingMapping: true  // Mevcut eşleştirmeyi kullan
                                        }, {
                                            responseType: 'blob'
                                        });
                                        
                                        // ZIP dosyasını indir
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `${selectedExamType}_Kitapciklar.zip`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        
                                        toast.dismiss('download-booklets');
                                        toast.success(`${bookletCount} kitapçık dosyası indirildi!`);
                                    } catch (error) {
                                        toast.dismiss('download-booklets');
                                        toast.error('İndirme hatası: ' + (error.response?.data?.error || error.message));
                                    }
                                }}
                                style={{
                                    backgroundColor: '#059669',
                                    color: 'white',
                                    padding: '0 20px',
                                    height: '44px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxSizing: 'border-box'
                                }}
                            >
                                <i className="fas fa-file-download"></i>
                                Kitapçık Bazında Soruları İndir ({bookletCount} Adet)
                            </button>
                        )}
                        
                        {/* Soruları Sil Butonu - En Sağda */}
                        {questions.length > 0 && (
                            <button
                                className="btn btn-danger"
                                onClick={() => setDeleteConfirm(true)}
                                style={{
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    padding: '0 20px',
                                    height: '44px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxSizing: 'border-box',
                                    marginLeft: 'auto'
                                }}
                            >
                                <i className="fas fa-trash-alt"></i>
                                Soruları Sil
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Desktop Table View */}
                <div className="questions-table-container">
                    <table className="questions-table">
                        <thead>
                            <tr>
                                <th>Soru No</th>
                                <th>Soru Metni</th>
                                <th>Puan</th>
                                <th>Sınav Türü</th>
                                <th>Durum</th>
                                {bookletCount > 1 && <th>Kitapçık Eşleşmesi</th>}
                                <th>Dersin öğrenme çıktıları</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map((question, index) => (
                                <React.Fragment key={index}>
                                <tr className="question-row">
                                    <td className="question-number">{index + 1}</td>
                                    <td className="question-text-cell">
                                        <div 
                                            className="question-text-content question-clickable"
                                            onClick={() => startEditingQuestion(question, index)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <MathText>{question.text}</MathText>
                                        </div>
                                    </td>
                                    <td className="question-score">
                                        <span 
                                            className="badge badge-secondary question-score-clickable"
                                            onClick={() => startEditingQuestion(question, index)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {question.score} puan
                                        </span>
                                    </td>
                                    <td className="question-exam-type">
                                        <span className={`badge ${examTypes.find(t => t.id === selectedExamType)?.color || 'badge-blue'}`}>
                                            {examTypes.find(t => t.id === selectedExamType)?.name || 'Sınav'}
                                        </span>
                                    </td>
                                    <td className="question-status">
                                        {(() => {
                                            const status = getQuestionStatus(question);
                                            return <span className={`badge ${status.className}`}>{status.text}</span>;
                                        })()}
                                    </td>
                                    {bookletCount > 1 && (
                                        <td className="question-booklet-map-cell">
                                            {(() => {
                                                const bookletMap = question.booklet_map || [];
                                                const BOOKLET_LETTERS = ['A', 'B', 'C', 'D'];
                                                const editingKey = `booklet_editing_${question.id}`;
                                                const isEditing = expandedQuestionMapping === editingKey;
                                                
                                                // A kitapçığındaki soru numarası (index + 1)
                                                const aNumber = index + 1;
                                                
                                                if (bookletMap.length === 0) {
                                                    return (
                                                        <button 
                                                            className="booklet-create-btn"
                                                            onClick={() => {
                                                                toast.info('Önce "Kitapçık Eşleştir" butonuna tıklayarak otomatik eşleştirme oluşturun', { duration: 4000 });
                                                            }}
                                                        >
                                                            <i className="fas fa-random"></i>
                                                            <span>Eşleşme Oluştur</span>
                                                        </button>
                                                    );
                                                }
                                                
                                                // Diğer kitapçıklardaki eşleşmeleri göster
                                                const mappings = bookletMap.slice(1).map((bNum, idx) => {
                                                    const letter = BOOKLET_LETTERS[idx + 1];
                                                    return { letter, num: bNum };
                                                });
                                                
                                                return (
                                                    <div className="booklet-map-container">
                                                        <div 
                                                            className={`booklet-map-display ${isEditing ? 'editing' : ''}`}
                                                            onClick={() => setExpandedQuestionMapping(isEditing ? null : editingKey)}
                                                        >
                                                            <span className="booklet-a-badge">A{aNumber}</span>
                                                            <span className="booklet-arrow">→</span>
                                                            <div className="booklet-targets">
                                                                {mappings.map(({ letter, num }) => (
                                                                    <span key={letter} className="booklet-target-badge">
                                                                        {letter}{num}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            <span className="booklet-edit-hint">
                                                                <i className="fas fa-pen"></i>
                                                                {isEditing ? 'Kapat' : 'Düzenle'}
                                                            </span>
                                                        </div>
                                                        
                                                        {isEditing && (
                                                            <div className="booklet-edit-panel">
                                                                {BOOKLET_LETTERS.slice(1, bookletCount).map((letter, idx) => {
                                                                    // Bu kitapçık için hangi değerler zaten kullanılıyor?
                                                                    const usedValues = {};
                                                                    const usedByQuestion = {};
                                                                    questions.forEach((q, qIdx) => {
                                                                        if (q.id !== question.id && q.booklet_map && q.booklet_map[idx + 1]) {
                                                                            usedValues[q.booklet_map[idx + 1]] = qIdx + 1; // A soru numarası
                                                                            usedByQuestion[q.booklet_map[idx + 1]] = q; // Soru objesi
                                                                        }
                                                                    });
                                                                    
                                                                    const currentValue = bookletMap[idx + 1] || '';
                                                                    
                                                                    return (
                                                                        <div key={letter} className="booklet-edit-row">
                                                                            <span className="booklet-edit-label">{letter} Kitapçığı:</span>
                                                                            <select
                                                                                value={currentValue}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onChange={async (e) => {
                                                                                    const newValue = parseInt(e.target.value);
                                                                                    if (isNaN(newValue)) return;
                                                                                    
                                                                                    // Çakışma kontrolü - takas öner
                                                                                    if (usedValues[newValue]) {
                                                                                        const conflictingQuestion = usedByQuestion[newValue];
                                                                                        
                                                                                        // Takas onay panelini göster
                                                                                        setSwapConfirm({
                                                                                            question: question,
                                                                                            questionIndex: index,
                                                                                            conflictingQuestion: conflictingQuestion,
                                                                                            conflictingANum: usedValues[newValue],
                                                                                            letter: letter,
                                                                                            idx: idx,
                                                                                            newValue: newValue,
                                                                                            oldValue: currentValue,
                                                                                            bookletMap: bookletMap
                                                                                        });
                                                                                        return;
                                                                                    }
                                                                                    
                                                                                    // Normal güncelleme (çakışma yok)
                                                                                    const newBookletMap = [...bookletMap];
                                                                                    while (newBookletMap.length <= idx + 1) {
                                                                                        newBookletMap.push(0);
                                                                                    }
                                                                                    newBookletMap[idx + 1] = newValue;
                                                                                    
                                                                                    try {
                                                                                        await axios.put(API_ENDPOINTS.UPDATE_QUESTION, {
                                                                                            id: question.id,
                                                                                            text: question.text,
                                                                                            score: question.score,
                                                                                            outcomes: question.outcomes || [],
                                                                                            bookletMap: newBookletMap
                                                                                        });
                                                                                        
                                                                                        setQuestions(prev => prev.map(q => 
                                                                                            q.id === question.id 
                                                                                                ? { ...q, booklet_map: newBookletMap }
                                                                                                : q
                                                                                        ));
                                                                                        
                                                                                        toast.success(`${letter} kitapçığı güncellendi`);
                                                                                    } catch (error) {
                                                                                        toast.error('Güncelleme hatası');
                                                                                    }
                                                                                }}
                                                                                className="booklet-edit-select"
                                                                            >
                                                                                {Array.from({ length: questions.length }, (_, i) => i + 1).map(num => {
                                                                                    const isUsed = usedValues[num];
                                                                                    const isCurrent = currentValue === num;
                                                                                    return (
                                                                                        <option 
                                                                                            key={num} 
                                                                                            value={num}
                                                                                        >
                                                                                            Soru {num}{isUsed && !isCurrent ? ` ⇄ A${isUsed}` : ''}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                    )}
                                    <td className="question-outcomes-cell">
                                        <div className="outcomes-summary-container">
                                            {renderOutcomeSummary(question)}
                                            <span 
                                                className="text-primary mappings-trigger"
                                                onClick={() => {
                                                    if (expandedQuestionMapping === index) {
                                                        setExpandedQuestionMapping(null);
                                                    } else {
                                                        setExpandedQuestionMapping(index);
                                                        if (learningOutcomes.length === 0 && selectedCourse && selectedCourse.courseId) {
                                                            fetchLearningOutcomes(selectedCourse.courseId);
                                                        }
                                                    }
                                                }}
                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                                <i className="fas fa-link" style={{ marginRight: '5px' }}></i>
                                                {expandedQuestionMapping === index ? 'Kapat' : 'Eşleştir'}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                                {expandedQuestionMapping === index && (
                                    <tr className="question-mapping-row">
                                        <td colSpan={bookletCount > 1 ? 7 : 6} className="mapping-cell">
                                            <div className="inline-mapping-container">
                                                <div className="mapping-header">
                                                    <h4>Soru {index + 1} - Dersin öğrenme çıktıları Eşleştirme</h4>
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={async () => {
                                                            if (!selectedCourse || !selectedCourse.courseId) {
                                                                toast.error('Lütfen bir ders seçin');
                                                                return;
                                                            }
                                                            setQuestionAiLoading(prev => ({ ...prev, [question.id]: true }));
                                                            try {
                                                                const response = await axios.post(API_ENDPOINTS.AI_MATCH_QUESTION, {
                                                                    question_text: question.text,
                                                                    course_id: selectedCourse.courseId
                                                                });
                                                                if (response.data.success) {
                                                                    setQuestionAiMatches(prev => ({
                                                                        ...prev,
                                                                        [question.id]: response.data.matches || []
                                                                    }));
                                                                    toast.success(`Yapay Zeka ${response.data.matches.length} Dersin Öğrenme Çıktısı önerdi!`);
                                                                }
                                                            } catch (error) {
                                                                toast.error('Yapay Zeka önerileri alınamadı');
                                                            } finally {
                                                                setQuestionAiLoading(prev => ({ ...prev, [question.id]: false }));
                                                            }
                                                        }}
                                                        disabled={questionAiLoading[question.id]}
                                                    >
                                                        {questionAiLoading[question.id] ? (
                                                            <>
                                                                <i className="fas fa-spinner fa-spin"></i> Analiz Ediliyor...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-brain"></i> Yapay Zeka Öner
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                                
                                                {questionAiMatches[question.id] && questionAiMatches[question.id].length > 0 && (
                                                    <div className="ai-suggestions-box">
                                                        <div className="ai-suggestions-header">
                                                            <i className="fas fa-brain"></i>
                                                            <strong>Yapay Zeka Önerileri:</strong>
                                                        </div>
                                                        <div className="ai-suggestions-list">
                                                            {questionAiMatches[question.id]
                                                                .sort((a, b) => (b.match_percentage || 0) - (a.match_percentage || 0))
                                                                .map((match, idx) => {
                                                                    const isSelected = question.outcomes && question.outcomes.includes(match.code);
                                                                    const percentage = match.match_percentage || 0;
                                                                    const color = percentage >= 70 ? '#16a34a' : percentage >= 50 ? '#22c55e' : '#94a3b8';
                                                                    const outcomeDetails = learningOutcomes.find(o => o.code === match.code);
                                                                    const descriptionText = outcomeDetails?.description || '';
                                                                    return (
                                                                        <div key={idx} className="ai-suggestion-item" style={{
                                                                            borderLeft: `3px solid ${color}`,
                                                                            backgroundColor: percentage >= 70 ? '#dcfce7' : percentage >= 50 ? '#f0fdf4' : '#f8fafc'
                                                                        }}>
                                                                            <div className="ai-suggestion-header">
                                                                                <span className="ai-suggestion-code">{match.code}</span>
                                                                                <span className="ai-suggestion-percentage" style={{ color }}>
                                                                                    %{percentage} Uyum
                                                                                </span>
                                                                            </div>
                                                                            {descriptionText && (
                                                                                <div className="ai-suggestion-description">
                                                                                    <strong>Açıklama:</strong> {descriptionText}
                                                                                </div>
                                                                            )}
                                                                            {match.reasoning && (
                                                                                <div className="ai-suggestion-reasoning">{match.reasoning}</div>
                                                                            )}
                                                                            {!isSelected && (
                                                                                <button
                                                                                    className="btn btn-sm btn-primary"
                                                                                    onClick={async () => {
                                                                                        const outcome = learningOutcomes.find(o => o.code === match.code);
                                                                                        if (outcome) {
                                                                                            await updateQuestionOutcomes(outcome.id, true, question.id);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    Ekle
                                                                                </button>
                                                                            )}
                                                                            {isSelected && (
                                                                                <span className="badge badge-success">✓ Eklendi</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {loadingOutcomes ? (
                                                    <div className="loading-message">Dersin öğrenme çıktıları yükleniyor...</div>
                                                ) : learningOutcomes.length === 0 ? (
                                                    <div className="empty-state">Bu ders için Dersin Öğrenme Çıktısı bulunamadı</div>
                                                ) : (
                                                    <div className="learning-outcomes-selection">
                                                        <h5>Dersin öğrenme çıktıları:</h5>
                                                        <Select
                                                            isMulti
                                                            options={learningOutcomes.map(lo => ({
                                                                value: lo.id,
                                                                label: `${lo.code}: ${lo.description}`,
                                                                code: lo.code
                                                            }))}
                                                            value={learningOutcomes
                                                                .filter(lo => question.outcomes && question.outcomes.includes(lo.code))
                                                                .map(lo => ({
                                                                    value: lo.id,
                                                                    label: `${lo.code}: ${lo.description}`,
                                                                    code: lo.code
                                                                }))}
                                                            onChange={async (selected) => {
                                                                const selectedCodes = selected.map(s => s.code);
                                                                // Mevcut seçimleri kaldır
                                                                for (const outcome of learningOutcomes) {
                                                                    const wasSelected = question.outcomes && question.outcomes.includes(outcome.code);
                                                                    const shouldBeSelected = selectedCodes.includes(outcome.code);
                                                                    if (wasSelected !== shouldBeSelected) {
                                                                        await updateQuestionOutcomes(outcome.id, shouldBeSelected, question.id);
                                                                    }
                                                                }
                                                            }}
                                                            placeholder="Dersin öğrenme çıktıları seçin..."
                                                            styles={{
                                                                control: (base) => ({
                                                                    ...base,
                                                                    minHeight: '40px',
                                                                    borderColor: '#023b58',
                                                                    '&:hover': { borderColor: '#023b58' }
                                                                }),
                                                                multiValue: (base) => ({
                                                                    ...base,
                                                                    backgroundColor: '#dcfce7'
                                                                }),
                                                                multiValueLabel: (base) => ({
                                                                    ...base,
                                                                    color: '#0f172a',
                                                                    fontWeight: 600
                                                                })
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Soru Düzenleme Modal */}
                {editingQuestion !== null && (
                    <div className="edit-modal-overlay">
                        <div className="edit-modal">
                            <div className="edit-modal-header">
                                <h3>Soru Düzenle</h3>
                                <button 
                                    className="close-btn"
                                    onClick={cancelEditing}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="edit-modal-content">
                                <div className="form-group">
                                    <label>Soru Metni:</label>
                                    <textarea
                                        value={editForm.text}
                                        onChange={(e) => handleEditFormChange('text', e.target.value)}
                                        rows="4"
                                        className="form-control"
                                        placeholder="Soru metnini girin..."
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Puan:</label>
                                    <input
                                        type="number"
                                        value={editForm.score}
                                        onChange={(e) => handleEditFormChange('score', e.target.value)}
                                        className="form-control"
                                        placeholder="Puanı girin..."
                                        min="0"
                                        max="100"
                                    />
                                </div>
                            </div>
                            <div className="edit-modal-footer">
                                <button 
                                    className="btn btn-outline"
                                    onClick={cancelEditing}
                                >
                                    İptal
                                </button>
                                <button 
                                    className="btn btn-success"
                                    onClick={saveQuestionEdit}
                                >
                                    Kaydet
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile Card View */}
                <div className="questions-cards-container">
                    {questions.map((question, index) => (
                        <div key={index} className="question-card">
                            <div className="question-header">
                                <div className="question-info">
                                    <h4>Soru {index + 1}</h4>
                                    <div className="question-details">
                                        <span className="badge badge-secondary">{question.score} puan</span>
                                        <span className={`badge ${examTypes.find(t => t.id === selectedExamType)?.color || 'badge-blue'}`}>
                                            {examTypes.find(t => t.id === selectedExamType)?.name || 'Sınav'}
                                        </span>
                                    </div>
                                </div>
                                <div className="question-status">
                                    {question.outcomes && question.outcomes.length > 0 ? (
                                        <span className="badge badge-success">Eşleştirildi</span>
                                    ) : (
                                        <span className="badge badge-danger">Eşleştirme bekleniyor</span>
                                    )}
                                </div>
                                <div className="question-actions">
                                    <button 
                                        className="btn btn-sm btn-outline"
                                        onClick={() => startEditingQuestion(question, index)}
                                        title="Soru Düzenle"
                                    >
                                        Düzenle
                                    </button>
                                </div>

                            </div>
                            <div className="question-content">
                                <div className="question-text">
                                    <MathText>{question.text}</MathText>
                                </div>
                                <div className="question-outcomes">
                                    {renderOutcomeSummary(question)}
                                </div>
                                {Number(bookletCount) === 2 && (
                                    <div className="question-booklet-map-mobile" style={{ marginTop: 8 }}>
                                        {(() => {
                                            const mappedB = bookletMappings ? bookletMappings[question.id] : null;
                                            if (mappedB || mappedB === 0) {
                                                return (
                                                    <span className="badge badge-outline">{`A${index + 1} ↔ B${mappedB}`}</span>
                                                );
                                            }
                                            return <span className="text-muted">Kitapçık eşleşmesi yok</span>;
                                        })()}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Soru düzenleme fonksiyonları
    const startEditingQuestion = (question, index) => {
        setEditingQuestion(index);
        setEditForm({
            text: question.text,
            score: question.score
        });
    };

    const cancelEditing = () => {
        setEditingQuestion(null);
        setEditForm({
            text: '',
            score: ''
        });
    };

    const saveQuestionEdit = async () => {
        if (editingQuestion !== null) {
            const question = questions[editingQuestion];
            
            try {
                // Veritabanında soruyu güncelle
                const response = await axios.put(API_ENDPOINTS.UPDATE_QUESTION, {
                    id: question.id,
                    text: editForm.text,
                    score: parseInt(editForm.score) || 0,
                    outcomes: question.outcomes || [],
                    bookletMap: question.booklet_map || null // Mevcut kitapçık eşleşmesini koru
                });

                if (response.data.success) {
                    // Local state'i güncelle
                    const updatedQuestions = [...questions];
                    updatedQuestions[editingQuestion] = {
                        ...updatedQuestions[editingQuestion],
                        text: editForm.text,
                        score: parseInt(editForm.score) || 0,
                        updated: true
                    };
                    setQuestions(updatedQuestions);
                    
                    // localStorage'ı güncelle
                    localStorage.setItem('questionOperations_questions', JSON.stringify(updatedQuestions));
                    
                    cancelEditing();
                    toast.success('Soru başarıyla güncellendi');
                } else {
                    console.error('Soru güncellenirken hata:', response.data.error);
                    toast.error('Soru güncellenirken bir hata oluştu: ' + response.data.error);
                }
            } catch (error) {
                console.error('Soru güncelleme hatası:', error);
                toast.error('Soru güncellenirken bir hata oluştu: ' + error.message);
            }
        }
    };

    const handleEditFormChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // eslint-disable-next-line no-unused-vars
    const deleteQuestion = (questionId) => {
        const newQuestions = questions.filter((_, index) => index !== questionId);
        setQuestions(newQuestions);
        
        // localStorage'ı güncelle
        localStorage.setItem('questionOperations_questions', JSON.stringify(newQuestions));
        
        // Eğer mevcut soru silindiyse, index'i sıfırla
        if (currentQuestionIndex >= newQuestions.length && newQuestions.length > 0) {
            setCurrentQuestionIndex(0);
            localStorage.setItem('questionOperations_currentIndex', '0');
        }
    };

    const checkAndLoadExistingQuestions = async () => {
        console.log('checkAndLoadExistingQuestions çağrıldı');
        if (!selectedProgram || !selectedCourse || !selectedExamType) {
            console.log('Gerekli parametreler eksik:', { selectedProgram, selectedCourse, selectedExamType });
            return;
        }
        
        try {
            console.log('Mevcut sorular kontrol ediliyor...', {
                programId: selectedProgram.programId,
                courseId: selectedCourse.courseId,
                examType: selectedExamType
            });
            
            const response = await axios.get(`${API_BASE_URL}/api/questions/${selectedProgram.programId}/${selectedCourse.courseId}?examType=${selectedExamType}`);
            
            console.log('API yanıtı:', response.data);
            
            if (response.data.success && response.data.questions.length > 0) {
                console.log(`${response.data.count} soru bulundu, yükleniyor...`);
                
                // Backend zaten doğru sınav türü için soruları getiriyor, ek filtreleme gerekmez
                const questions = response.data.questions;
                
                // Soruları state'e set et
                setQuestions(questions);
                setCurrentQuestionIndex(0);

                // DB'deki booklet_map'lerden başlangıç eşlemesini kur
                try {
                    const nextMap = {};
                    questions.forEach(q => {
                        if (q.booklet_map && Array.isArray(q.booklet_map) && q.booklet_map.length > 0) {
                            // tek değer kabul edilir; çoklu ise ilkini al
                            const first = parseInt(q.booklet_map[0]);
                            if (!isNaN(first)) nextMap[q.id] = first;
                        }
                    });
                    if (Object.keys(nextMap).length > 0) {
                        setBookletMappings(nextMap);
                        saveBookletMappings(nextMap);
                    }
                } catch (e) {}
                
                // localStorage'ı güncelle
                localStorage.setItem('questionOperations_questions', JSON.stringify(questions));
                localStorage.setItem('questionOperations_currentIndex', '0');
                localStorage.setItem('questionOperations_activeTab', 'questions');
                
                // Sorular tabına geç
                setActiveTab('questions');
            } else {
                console.log(`${selectedExamType} sınav türü için soru bulunamadı`);
                // Soruları temizle
                setQuestions([]);
                setCurrentQuestionIndex(0);
                localStorage.removeItem('questionOperations_questions');
                localStorage.removeItem('questionOperations_currentIndex');
                localStorage.setItem('questionOperations_currentIndex', '0');
                
                setActiveTab('upload');
            }
        } catch (error) {
            console.error('Sorular yüklenirken hata:', error);
            // Hata durumunda soruları temizle
            setQuestions([]);
            setCurrentQuestionIndex(0);
            localStorage.removeItem('questionOperations_questions');
            localStorage.removeItem('questionOperations_currentIndex');
            localStorage.setItem('questionOperations_currentIndex', '0');
            
            setActiveTab('upload');
        }
    };

    const updateQuestionOutcomes = async (outcomeId, checked, questionId = null, questionsArray = null) => {
        console.log('updateQuestionOutcomes called:', { outcomeId, checked, questionId }); // Debug
        
        const outcome = learningOutcomes.find(o => o.id === outcomeId);
        console.log('Found outcome:', outcome); // Debug
        
        if (!outcome) return;

        // questionsArray parametresi verilmişse onu kullan, yoksa state'ten al
        const currentQuestions = questionsArray || questions;
        const updatedQuestions = [...currentQuestions];
        
        // questionId verilmişse o soruyu bul, yoksa currentQuestionIndex'i kullan
        let targetQuestion;
        if (questionId !== null && questionId !== undefined) {
            targetQuestion = updatedQuestions.find(q => String(q.id) === String(questionId));
            console.log(`updateQuestionOutcomes: questionId=${questionId}, found:`, targetQuestion ? 'yes' : 'no');
        } else {
            targetQuestion = updatedQuestions[currentQuestionIndex];
            console.log(`updateQuestionOutcomes: using currentQuestionIndex=${currentQuestionIndex}`);
        }
        
        console.log('Target question before update:', targetQuestion); // Debug
        
        if (!targetQuestion) {
            console.error('Target question is undefined, cannot update. questionId:', questionId, 'currentQuestionIndex:', currentQuestionIndex);
            return;
        }

        if (!targetQuestion.outcomes) {
            targetQuestion.outcomes = [];
        }

        console.log('Current outcomes:', targetQuestion.outcomes); // Debug
        console.log('Outcome code to add/remove:', outcome.code); // Debug

        if (checked) {
            if (!targetQuestion.outcomes.includes(outcome.code)) {
                targetQuestion.outcomes.push(outcome.code);
                console.log('Added outcome:', outcome.code); // Debug
            }
        } else {
            // ÖÇ kaldırılıyor
            targetQuestion.outcomes = targetQuestion.outcomes.filter(o => o !== outcome.code);
            console.log('Removed outcome:', outcome.code); // Debug
            
            // Eğer kaldırılan outcome bir ÖÇ ise, ilgili PÇ'leri kontrol et ve kaldır
            if (outcome.code.startsWith('ÖÇ')) {
                // Kalan ÖÇ'leri bul
                const remainingLearningOutcomes = targetQuestion.outcomes.filter(o => o.startsWith('ÖÇ'));
                
                // Bu ÖÇ'ye bağlı PÇ'leri bul
                if (selectedCourse && selectedProgram) {
                    try {
                        const response = await axios.get(`${API_BASE_URL}/api/outcome-mappings/${selectedProgram.programId}/${selectedCourse.courseId}/${outcomeId}`);
                        if (response.data && response.data.success && response.data.mapping) {
                            const removedProgramOutcomeIds = response.data.mapping.program_outcome_ids || [];
                            
                            // Kalan ÖÇ'lerin tüm PÇ'lerini topla
                            const remainingProgramOutcomeIds = new Set();
                            
                            for (const remainingLOCode of remainingLearningOutcomes) {
                                // ÖÇ kodundan ID'yi çıkar (ÖÇ1 -> 1)
                                const loNumber = parseInt(remainingLOCode.replace('ÖÇ', ''));
                                if (!isNaN(loNumber)) {
                                    // Bu ÖÇ'nin ID'sini bul
                                    const remainingLO = learningOutcomes.find(lo => {
                                        const loNum = parseInt(lo.number) || parseInt(lo.code?.replace('ÖÇ', ''));
                                        return loNum === loNumber || lo.code === remainingLOCode;
                                    });
                                    
                                    if (remainingLO) {
                                        try {
                                            const loMappingResponse = await axios.get(`${API_BASE_URL}/api/outcome-mappings/${selectedProgram.programId}/${selectedCourse.courseId}/${remainingLO.id}`);
                                            if (loMappingResponse.data && loMappingResponse.data.success && loMappingResponse.data.mapping) {
                                                const loProgramOutcomeIds = loMappingResponse.data.mapping.program_outcome_ids || [];
                                                loProgramOutcomeIds.forEach(poId => remainingProgramOutcomeIds.add(poId));
                                            }
                                        } catch (err) {
                                            // Hata durumunda devam et
                                        }
                                    }
                                }
                            }
                            
                            // Kaldırılan ÖÇ'nin PÇ'lerinden, kalan ÖÇ'lerde olmayanları kaldır
                            for (const poId of removedProgramOutcomeIds) {
                                const poCode = `PÇ${poId}`;
                                
                                // Eğer bu PÇ kalan ÖÇ'lerden hiçbirine bağlı değilse, kaldır
                                if (!remainingProgramOutcomeIds.has(poId)) {
                                    targetQuestion.outcomes = targetQuestion.outcomes.filter(o => o !== poCode);
                                    console.log(`PÇ${poId} kaldırıldı çünkü kalan ÖÇ'lere bağlı değil`);
                                } else {
                                    console.log(`PÇ${poId} korundu çünkü kalan ÖÇ'lere bağlı`);
                                }
                            }
                        }
                    } catch (error) {
                        console.log('PÇ kaldırma sırasında hata:', error);
                        // Hata olsa bile devam et
                    }
                }
            }
        }

        console.log('Updated question outcomes:', targetQuestion.outcomes); // Debug
        
        // Eğer tüm learning outcomes kaldırıldıysa, program outcomes'u da temizle
        const learningOutcomeCodes = targetQuestion.outcomes.filter(o => o.startsWith('ÖÇ'));
        if (learningOutcomeCodes.length === 0) {
            // Tüm program outcomes'ları kaldır
            targetQuestion.outcomes = targetQuestion.outcomes.filter(o => !o.startsWith('PÇ'));
            console.log('Tüm learning outcomes kaldırıldı, program outcomes da temizlendi');
        }
        
        // Ders Dersin Öğrenme Çıktısı seçildiğinde, backend'ten program çıktılarını da al
        if (checked && selectedCourse && selectedProgram) {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/outcome-mappings/${selectedProgram.programId}/${selectedCourse.courseId}/${outcomeId}`);
                
                if (response.data && response.data.success && response.data.mapping) {
                    const programOutcomeIds = response.data.mapping.program_outcome_ids || [];
                    console.log('Bulunan program çıktıları:', programOutcomeIds);
                    
                    // Program çıktılarını da ekle
                    if (programOutcomeIds.length > 0) {
                        // Program çıktısı kodlarını al
                        const programOutcomeCodes = [];
                        for (const poId of programOutcomeIds) {
                            // Program çıktısı kodunu bul (PÇ1, PÇ2, vb.)
                            const poCode = `PÇ${poId}`; // Basit mapping, gerçek uygulamada veritabanından alınmalı
                            if (!targetQuestion.outcomes.includes(poCode)) {
                                targetQuestion.outcomes.push(poCode);
                                programOutcomeCodes.push(poCode);
                            }
                        }
                        
                        // Outcomes'ları sırala ve tekrarları kaldır
                        targetQuestion.outcomes = [...new Set(targetQuestion.outcomes)].sort();
                        
                        if (programOutcomeCodes.length > 0) {
                            console.log('Otomatik eklenen program çıktıları:', programOutcomeCodes);
                        }
                    }
                }
            } catch (error) {
                console.log('Program çıktıları alınırken hata:', error);
                // Hata durumunda devam et
            }
        }
        
        // Durum güncellemesi: outcomes değiştiğinde saved durumunu kontrol et
        const hasLearningOutcomes = targetQuestion.outcomes && targetQuestion.outcomes.filter(o => o.startsWith('ÖÇ')).length > 0;
        // Eğer outcomes varsa ve daha önce kaydedilmişse, updated olarak işaretle
        if (hasLearningOutcomes && targetQuestion.saved) {
            targetQuestion.updated = true;
        }
        
        setQuestions(updatedQuestions);
        
        // localStorage'ı güncelle
        localStorage.setItem('questionOperations_questions', JSON.stringify(updatedQuestions));

        // Backend'e kaydet - ÖÇ kaldırıldığında da mutlaka kaydet
        if (!targetQuestion.id) {
            console.warn('Soru ID bulunamadı, backend\'e kayıt yapılamıyor. Soru:', targetQuestion);
            toast.error('Soru ID bulunamadı. Lütfen sayfayı yenileyin.');
            return;
        }
        
        try {
            console.log('Backend\'e kaydediliyor - Soru ID:', targetQuestion.id, 'Güncel outcomes:', targetQuestion.outcomes);
            const response = await axios.put(`${API_ENDPOINTS.UPDATE_QUESTION}`, {
                id: targetQuestion.id,
                text: targetQuestion.text,
                score: targetQuestion.score,
                outcomes: targetQuestion.outcomes || [], // Güncel outcomes (kaldırılan ÖÇ dahil değil)
                examType: selectedExamType,
                bookletMap: targetQuestion.booklet_map || null // Mevcut kitapçık eşleşmesini koru
            });
            
            console.log('Backend kayıt başarılı - Soru ID:', targetQuestion.id, 'Response:', response.data);
            
            // Soruyu saved olarak işaretle ve durumu güncelle
            setQuestions(prevQuestions => {
                const finalUpdatedQuestions = [...prevQuestions];
                const finalTargetQuestion = finalUpdatedQuestions.find(q => q.id === targetQuestion.id);
                if (finalTargetQuestion) {
                    finalTargetQuestion.saved = true;
                    // Eğer learning outcomes varsa, tamamlandı olarak işaretle
                    const hasLO = finalTargetQuestion.outcomes && finalTargetQuestion.outcomes.filter(o => o.startsWith('ÖÇ')).length > 0;
                    if (hasLO) {
                        finalTargetQuestion.updated = true;
                    } else {
                        // Learning outcomes yoksa, updated flag'ini kaldır
                        finalTargetQuestion.updated = false;
                    }
                }
                localStorage.setItem('questionOperations_questions', JSON.stringify(finalUpdatedQuestions));
                return finalUpdatedQuestions;
            });
            
            // Başarı mesajı göster
            if (!checked) {
                toast.success(`ÖÇ kaldırıldı ve veritabanına kaydedildi (Soru ID: ${targetQuestion.id})`);
            }
        } catch (error) {
            console.error('Soru kaydedilirken hata - Soru ID:', targetQuestion.id, 'Hata:', error);
            console.error('Hata detayı:', error.response?.data || error.message);
            console.error('Gönderilen veri:', {
                id: targetQuestion.id,
                outcomes: targetQuestion.outcomes,
                text: targetQuestion.text,
                score: targetQuestion.score
            });
            
            // Hata olsa bile state güncellenmiş olur, ancak kullanıcıya bildir
            const errorMessage = error.response?.data?.error || error.message || 'Bilinmeyen hata';
            toast.error(`ÖÇ kaldırılırken veritabanına kayıt yapılamadı (Soru ID: ${targetQuestion.id}): ${errorMessage}`);
        }

        // UI'ı hemen tazele (ÖÇ rozetleri ve durumlar anında görünsün)
        try { /* renderMappingContent removed */ } catch (e) {}
    };

    const goToNextQuestion = async () => {
        const currentQuestion = questions[currentQuestionIndex];
        
        if (!currentQuestion) return;
        
        // Check if at least one outcome is selected
        if (!currentQuestion.outcomes || currentQuestion.outcomes.length === 0) {
            showWarningToast();
            return;
        }

        // Mevcut ders ile session storage'daki ders aynı mı kontrol et
        const currentCourseId = selectedCourse ? selectedCourse.courseId : null;
        const sessionCourseId = sessionStorage.getItem('current_course_id');
        
        if (currentCourseId && sessionCourseId && currentCourseId.toString() !== sessionCourseId) {
            alert('Farklı bir ders seçildi. Lütfen önce bu ders için soru yükleyin.');
            return;
        }

        try {
            // Mevcut soruyu veritabanında güncelle
            console.log('Soru güncelleniyor:', currentQuestion); // Debug
            
            const response = await axios.put(`${API_ENDPOINTS.UPDATE_QUESTION}`, {
                id: currentQuestion.id,
                text: currentQuestion.text,
                score: currentQuestion.score,
                outcomes: currentQuestion.outcomes,
                examType: selectedExamType,
                bookletMap: currentQuestion.booklet_map || null // Mevcut kitapçık eşleşmesini koru
            });

            console.log('Soru güncelleme yanıtı:', response.data); // Debug

            if (response.data.success) {
                console.log(`Soru ${currentQuestionIndex + 1} başarıyla güncellendi`); // Debug
                
                // Local state'i güncelle
                const updatedQuestions = [...questions];
                updatedQuestions[currentQuestionIndex] = {
                    ...currentQuestion,
                    saved: true,
                    updated: true
                };
                setQuestions(updatedQuestions);
                
                // localStorage'ı güncelle
                localStorage.setItem('questionOperations_questions', JSON.stringify(updatedQuestions));
            } else {
                console.error('Soru güncellenirken hata:', response.data.error);
                alert('Soru güncellenirken bir hata oluştu: ' + response.data.error);
                return;
            }

        } catch (error) {
            console.error('Soru güncelleme hatası:', error);
            alert('Soru güncellenirken bir hata oluştu: ' + error.message);
            return;
        }

        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            // renderMappingContent removed
            
            // localStorage'ı güncelle
            localStorage.setItem('questionOperations_currentIndex', (currentQuestionIndex + 1).toString());
        }
    };

    const goToPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
            // renderMappingContent removed
            
            // localStorage'ı güncelle
            localStorage.setItem('questionOperations_currentIndex', (currentQuestionIndex - 1).toString());
        }
    };

    const completeMapping = async () => {
        const currentQuestion = questions[currentQuestionIndex];
        
        if (!currentQuestion) return;
        
        // Check if at least one outcome is selected
        if (!currentQuestion.outcomes || currentQuestion.outcomes.length === 0) {
            showWarningToast();
            return;
        }

        // Mevcut ders ile session storage'daki ders aynı mı kontrol et
        const currentCourseId = selectedCourse ? selectedCourse.courseId : null;
        const sessionCourseId = sessionStorage.getItem('current_course_id');
        
        if (currentCourseId && sessionCourseId && currentCourseId.toString() !== sessionCourseId) {
            alert('Farklı bir ders seçildi. Lütfen önce bu ders için soru yükleyin.');
            return;
        }

        try {
            // Son soruyu da güncelle
            console.log('Son soru güncelleniyor:', currentQuestion); // Debug
            
            const response = await axios.put(`${API_ENDPOINTS.UPDATE_QUESTION}`, {
                id: currentQuestion.id,
                text: currentQuestion.text,
                score: currentQuestion.score,
                outcomes: currentQuestion.outcomes,
                examType: selectedExamType,
                bookletMap: currentQuestion.booklet_map || null // Mevcut kitapçık eşleşmesini koru
            });

            console.log('Son soru güncelleme yanıtı:', response.data); // Debug

            if (response.data.success) {
                alert('Tüm sorular başarıyla eşleştirildi ve kaydedildi!');
                
                // Local state'i güncelle
                const updatedQuestions = [...questions];
                updatedQuestions[currentQuestionIndex] = {
                    ...currentQuestion,
                    saved: true,
                    updated: true
                };
                setQuestions(updatedQuestions);
                
                // localStorage'ı güncelle (soruları silme)
                localStorage.setItem('questionOperations_activeTab', 'questions');
                localStorage.removeItem('questionOperations_fileInfo');
                
                // Session storage'ı koru
                // sessionStorage.removeItem('current_course_id'); // Bu satırı kaldır
                
                // setQuestions([]); // Bu satırı kaldır - soruları koru
                setActiveTab('questions'); // upload yerine questions
                // setCurrentQuestionIndex(0); // Bu satırı kaldır
                setFileInfo(null);
                // setSelectedProgram(null); // Bu satırı kaldır
                // setSelectedCourse(null); // Bu satırı kaldır
                
                // Veritabanındaki sorular korunur - eşleştirme tamamlandığında
                console.log('Eşleştirme tamamlandı, sorular veritabanında korunuyor');
            } else {
                alert('Son soru kaydedilirken bir hata oluştu: ' + response.data.error);
            }
        } catch (error) {
            console.error('Son soru kaydetme hatası:', error);
            alert('Son soru kaydedilirken bir hata oluştu: ' + error.message);
        }
    };

    const showWarningToast = () => {
        setShowMappingWarning(true);
        setWarningCountdown(3);
        setWarningProgress(0);

        const timer = setInterval(() => {
            setWarningCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setShowMappingWarning(false);
                    return 3;
                }
                return prev - 1;
            });
        }, 1000);

        const progressTimer = setInterval(() => {
            setWarningProgress(prev => {
                if (prev >= 100) {
                    clearInterval(progressTimer);
                    return 0;
                }
                return prev + 3.33;
            });
        }, 100);
    };

    const closeWarningToast = () => {
        setShowMappingWarning(false);
        setWarningCountdown(3);
        setWarningProgress(0);
    };

    // AI Eşleştirme Fonksiyonları
    const handleAISuggest = async () => {
        console.log('Yapay Zeka Öner basıldı - Debug:', {
            questionsLength: questions.length,
            currentIndex: currentQuestionIndex,
            selectedCourse: selectedCourse?.courseId,
            currentQuestion: questions[currentQuestionIndex]
        });
        
        // Kontroller
        if (!questions || questions.length === 0) {
            toast.error('Önce sorular yüklenmelidir');
            return;
        }
        
        if (currentQuestionIndex < 0 || currentQuestionIndex >= questions.length) {
            toast.error('Geçersiz soru indeksi');
            return;
        }
        
        const currentQuestion = questions[currentQuestionIndex];
        
        if (!currentQuestion) {
            toast.error('Soru bulunamadı');
            console.error('currentQuestion undefined:', { currentQuestionIndex, questionsLength: questions.length });
            return;
        }
        
        if (!currentQuestion.text) {
            toast.error('Soru metni bulunamadı');
            console.error('currentQuestion.text undefined:', currentQuestion);
            return;
        }
        
        if (!selectedCourse || !selectedCourse.courseId) {
            toast.error('Lütfen bir ders seçin');
            return;
        }
        
        console.log('Yapay Zeka analiz başlatılıyor:', {
            questionText: currentQuestion.text.substring(0, 50) + '...',
            courseId: selectedCourse.courseId
        });
        
        // Önceki AI analizini iptal et
        if (aiControllerRef.current) {
            aiControllerRef.current.abort();
        }
        
        // Yeni abort controller oluştur
        const controller = new AbortController();
        aiControllerRef.current = controller;
        
        setLoadingAI(true);
        setAiError(null);
        
        try {
            const response = await axios.post(API_ENDPOINTS.AI_MATCH_QUESTION, {
                question_text: currentQuestion.text,
                course_id: selectedCourse.courseId
            }, {
                signal: controller.signal
            });
            
            console.log('Yapay Zeka Yanıtı:', response.data);
            
            if (response.data.success) {
                setAiMatches(response.data.matches);
                toast.success(`Yapay Zeka ${response.data.matches.length} dersin Dersin Öğrenme Çıktısı için öneri oluşturdu!`);
            } else {
                setAiError('Yapay Zeka önerileri alınamadı');
                toast.error('Yapay Zeka önerileri alınamadı');
            }
        } catch (error) {
            // Abort edilmişse sessizce geç
            if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
                console.log('Yapay Zeka analizi iptal edildi');
                return;
            }
            
            console.error('Yapay Zeka eşleştirme hatası:', error);
            setAiError(error.response?.data?.error || 'Yapay Zeka önerileri alınırken hata oluştu');
            toast.error('Yapay Zeka önerileri alınırken hata oluştu');
        } finally {
            setLoadingAI(false);
            aiControllerRef.current = null;
        }
    };
    
    const getAIMatchPercentage = (outcomeCode) => {
        const match = aiMatches.find(m => m.code === outcomeCode);
        return match ? match.match_percentage : null;
    };
    
    const getMatchColor = (percentage) => {
        if (percentage >= 70) return '#16a34a'; // Koyu yeşil
        if (percentage >= 50) return '#22c55e'; // Yeşil
        return '#94a3b8'; // Gri
    };
    
    const getMatchBgColor = (percentage) => {
        if (percentage >= 70) return '#dcfce7'; // Açık yeşil
        if (percentage >= 50) return '#f0fdf4'; // Çok açık yeşil
        return '#f8fafc'; // Açık gri
    };



    return (
        <div className="question-management-container">
            {/* Warning Toast */}
            {showMappingWarning && (
                <div className="warning-toast">
                    <div className="warning-overlay"></div>
                    <div className="warning-content">
                        <div className="warning-header">
                            <div className="warning-icon">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                                                            <div className="warning-text">
                                    <h3>Eşleştirme Gerekli</h3>
                                    <p>Bu soruyu en az bir ders Dersin Öğrenme Çıktısı ile eşleştirmelisiniz.</p>
                                </div>
                            <button className="warning-close" onClick={closeWarningToast}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="warning-progress">
                            <div className="progress-info">
                                <span>Otomatik kapanma:</span>
                                <span>{warningCountdown} saniye</span>
                            </div>
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${warningProgress}%` }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mapping Error Modal */}
            {showMappingError && (
                <div className="warning-overlay">
                    <div className="warning-modal mapping-error-modal">
                        <div className="warning-header">
                            <div className="warning-icon mapping-error">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <div className="warning-content">
                                <h3>Çıktı Eşleştirme Uyarısı</h3>
                                <p>{mappingErrorMessage}</p>
                                {mappingStatus && !mappingStatus.is_mapped && mappingStatus.missing_mappings && mappingStatus.missing_mappings.length > 0 && (
                                    <div className="missing-mappings">
                                        <h4>Eşleştirilmemiş Dersin öğrenme çıktıları:</h4>
                                        <ul>
                                            {mappingStatus.missing_mappings.map((outcome) => (
                                                <li key={outcome.id}>
                                                    <strong>{outcome.code}:</strong> {outcome.description}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <p><strong>Çözüm:</strong> Dersin öğrenme çıktılarını program çıktıları ile eşleştirmeniz gerekiyor. Ders bilgilerinden ilgili dersin çıktıları eşleştirmesini tamamlayın.</p>
                                <div className="mapping-actions">
                                    <div className="mapping-action-buttons">
                                        <button 
                                            className="btn btn-primary mapping-redirect-btn"
                                            onClick={() => {
                                                closeMappingError();
                                                navigate('/course-contents');
                                            }}
                                        >
                                            <i className="fas fa-book"></i>
                                            Ders Bilgilerine Git
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <button className="warning-close" onClick={closeMappingError}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}


            <div className="page-header">
                <h1>Soru Yönetim Sistemi</h1>
            </div>

            <div className="tabs">
                <div className="tab-list">
                    <button 
                        className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
                        onClick={() => switchTab('upload')}
                    >
                        <i className="fas fa-upload"></i>
                        Excel Yükle
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'questions' ? 'active' : ''}`}
                        onClick={() => switchTab('questions')}
                    >
                        <i className="fas fa-eye"></i>
                        Sorular ({updateQuestionCount()})
                    </button>

                </div>

                {/* Upload Tab */}
                {activeTab === 'upload' && (
                    <div className="tab-content active">
                        <div className="card">
                            <div className="card-header">
                                <div className="header-content">
                                    <div>
                                        <h2><i className="fas fa-file-excel"></i> Excel Dosyası Yükle</h2>
                                    </div>
                                    <div className="header-actions">
                                        <div className="sample-hint">Soruları yüklemek için Excel yapısı</div>
                                        <button 
                                            className="sample-download-btn"
                                            onClick={downloadSampleTemplate}
                                            title="Örnek şablonu indir"
                                        >
                                            <i className="fas fa-download"></i>
                                            İndir
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-content">

                                <div className="form-group">
                                    <label htmlFor="assigned-course">Ders (Sadece Atananlar)</label>
                                    <Select
                                        inputId="assigned-course"
                                        classNamePrefix="assigned-course"
                                        placeholder="Ders seçin..."
                                        isSearchable
                                        menuIsOpen={isCourseMenuOpen}
                                        onMenuOpen={() => setIsCourseMenuOpen(true)}
                                        onMenuClose={() => setIsCourseMenuOpen(false)}
                                        options={uniqueAssignedCourses.map(c => ({
                                            value: String(c.courseId),
                                            label: formatCourseLabel(c),
                                            meta: c
                                        }))}
                                        value={selectedCourse ? [{
                                            value: String(selectedCourse.courseId),
                                            label: (() => {
                                                const it = uniqueAssignedCourses.find(c => String(c.courseId) === String(selectedCourse.courseId));
                                                return it ? formatCourseLabel(it) : '';
                                            })(),
                                            meta: selectedCourse
                                        }][0] : null}
                                        onChange={(opt) => {
                                            const val = opt ? opt.value : '';
                                            const item = uniqueAssignedCourses.find(c => String(c.courseId) === String(val));
                                            if (item) {
                                                handleProgramChange({ programId: item.programId, programName: item.programName });
                                                handleCourseChange({ courseId: item.courseId, courseCode: item.courseCode, courseName: item.courseName });
                                            } else {
                                                handleCourseChange(null);
                                            }
                                            setIsCourseMenuOpen(false);
                                        }}
                                        styles={{
                                            container: base => ({ ...base, width: '100%' }),
                                            control: base => ({ ...base, minHeight: 56, borderRadius: 14 })
                                        }}
                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                        maxMenuHeight={380}
                                    />
                                    {loadingCourses && <small className="text-muted">Dersler yükleniyor...</small>}
                                    {!loadingCourses && academicCourses.length === 0 && (
                                        <small className="text-danger">Size atanmış ders bulunamadı</small>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="exam-type">Sınav Türü Seçin</label>
                                    <Select
                                        inputId="exam-type"
                                        classNamePrefix="exam-type"
                                        placeholder="Sınav türünü seçin..."
                                        isDisabled={!selectedCourse}
                                        isSearchable={false}
                                        options={availableExamTypesRaw.map(raw => {
                                            const canon = normalizeExamTypeCanon(raw);
                                            const found = examTypes.find(t => t.id === canon);
                                            const label = found ? found.name : (raw.charAt(0).toUpperCase() + raw.slice(1));
                                            return { value: canon, label };
                                        })}
                                        value={selectedExamType ? [{
                                            value: selectedExamType,
                                            label: (() => {
                                                const found = examTypes.find(t => t.id === selectedExamType);
                                                if (found) return found.name;
                                                const raw = availableExamTypesRaw.find(r => normalizeExamTypeCanon(r) === selectedExamType) || '';
                                                return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
                                            })()
                                        }][0] : null}
                                        onChange={(opt) => handleExamTypeChange(opt ? opt.value : '')}
                                        styles={{
                                            container: base => ({ ...base, width: '100%' }),
                                            control: base => ({ ...base, minHeight: 56, borderRadius: 14 })
                                        }}
                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                        maxMenuHeight={320}
                                    />
                                    {!selectedCourse && <small className="text-muted">Önce ders seçiniz</small>}
                                </div>

                                {selectedExamType && (
                                    <div className="form-group">
                                        <label htmlFor="booklet-count">Kitapçık Sayısı</label>
                                        <select
                                            id="booklet-count"
                                            className="form-select"
                                            disabled={!selectedCourse || !selectedExamType}
                                            value={bookletCount}
                                            onChange={(e) => handleBookletCountChange(e.target.value)}
                                        >
                                            {[1,2,3,4].map((n) => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                        <small className="text-muted">Kaç adet kitapçık olacağını seçiniz</small>
                                    </div>
                                )}

                                <div className="file-upload-area">
                                    <i className="fas fa-file-excel"></i>
                                    <div className="upload-text">
                                        <label htmlFor="excel-file" className="upload-label">
                                            <span>Excel dosyası seçin</span>
                                            <input 
                                                type="file" 
                                                id="excel-file" 
                                                accept=".xlsx,.xls" 
                                                className="hidden"
                                                onChange={handleFileUpload}
                                            />
                                        </label>
                                        <p>Desteklenen formatlar: .xlsx, .xls</p>
                                    </div>
                                </div>

                                {fileInfo && (
                                    <div className="file-info">
                                        <div className="file-details">
                                            <div>
                                                <p className="file-name-label">Seçilen dosya:</p>
                                                <p className="file-name">{fileInfo.name}</p>
                                            </div>
                                            <button 
                                                className="btn btn-primary"
                                                onClick={processExcelFile}
                                                disabled={isUploading || !updateProcessButton()}
                                            >
                                                <span>{isUploading ? 'İşleniyor...' : 'Dosyayı İşle'}</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Questions Tab */}
                {activeTab === 'questions' && (
                    <div className="tab-content active">
                        <div className="card">
                            <div className="card-content">
                                {renderQuestionsList()}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default QuestionManagement;
